import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiCallEx } from "../../../apiClient";
import {
  DocumentIntelligenceSourcePicker,
  type DocumentIntelligenceSourceAvailability,
  type DocumentIntelligenceSourceItem,
  type DocumentIntelligenceSourceTab,
} from "../DocumentIntelligenceSourcePicker";
import {
  AssistantScreen,
  BlankDraftScreen,
  LoadedDraftScreen,
  ThreadScreen,
  type DraftChange,
  type DraftClause,
  type CoreMention,
  type CorePrompt,
  type KnowledgeSourcesSelection,
  type ThreadCitation,
} from "./screens/core";
import {
  HistoryScreen,
  ProjectDetailScreen,
  ProjectNewScreen,
  ProjectsScreen,
  SettingsScreen,
  WorkflowBuilderScreen,
  WorkflowRunScreen,
  WorkflowsScreen,
  type WorkflowBuilderSaveInput,
} from "./screens/operations";
import { PlaybookCreationFlow } from "./screens/review/PlaybookCreation";
import { PlaybookPositionsEditor } from "./screens/review/PlaybookEditor";
import { PlaybooksList } from "./screens/review/PlaybooksList";
import { ReviewTableSetup } from "./screens/review/ReviewTableSetup";
import { ReviewTableWorkspace } from "./screens/review/ReviewTableWorkspace";
import { SourcesLibrary } from "./screens/review/SourcesLibrary";
import type {
  PlaybookListItem,
  PlaybookPosition,
  ReviewColumn,
  ReviewDocument,
  ReviewRow,
  ReviewTableSettings,
  SourceLibraryItem,
} from "./screens/review/types";
import {
  activityStateFromStoredProgress,
  initialActivityState,
  openActivityStream,
  redlineProgressStreamPath,
  reduceActivityEvent,
  type ActivityConnection,
  type ActivityStreamState,
} from "./shared/activityStream";
import { documentIntelligenceApi } from "./shared/api";
import {
  navigateDocumentIntelligence,
  parseDocumentIntelligenceRoute,
} from "./shared/router";
import { readShowWorking, writeShowWorking } from "./shared/showWorkingPreference";
import type {
  DocumentIntelligenceHistoryItem,
  DocumentIntelligenceMessage,
  DocumentIntelligenceNotice,
  DocumentIntelligencePlaybook,
  DocumentIntelligenceProject,
  DocumentIntelligenceSourceMode,
  DocumentIntelligenceThread,
  DocumentIntelligenceWorkflow,
} from "./shared/types";
import "./document-intelligence.css";

type User = { id?: string | null; name?: string | null; email?: string | null } | null;
type PickerPurpose = "assistant" | "draft" | "review";
// An attachment's ROLE is what the author said it is for, never inferred from the
// order it happened to be uploaded in. "unstated" is a real state and is the one
// that makes the assistant ask rather than guess.
type AttachmentRole = "under_review" | "reference" | "unstated";

// A document offered to the model as context. The ASSISTANT screen uses this shape
// directly: there, everything attached is simply context and there is nothing to
// disambiguate, so it has no role.
type ContextDocument = { versionId: string; name: string; source: string };

// A DRAFT attachment is a context document that also carries what the author said it
// is FOR. Keeping these as separate types stops a role being invented for the
// assistant screen, where the concept does not exist and any value would be a lie.
type DraftSource = ContextDocument & { role: AttachmentRole };

// The @-tokens the author actually typed. "@oneNDA" is a token; so is
// "@CDA" in "@CDA Playbook.docx" — a token stops at whitespace, because that is
// where typing stops when the author is writing a sentence rather than pasting a
// filename. Trailing sentence punctuation is discarded so "@oneNDA." matches.
// Below this, a fragment identifies nothing. Applied to BOTH sides of the prefix
// comparison — applying it to only one is what let a one-character filename match.
const MIN_MATCH_LENGTH = 3;

function mentionTokens(instruction: string): string[] {
  return [...String(instruction || "").matchAll(/@([^\s@]+)/g)]
    .map((match) => match[1].replace(/[.,;:!?)\]]+$/, "").toLowerCase())
    .filter((token) => token.length >= MIN_MATCH_LENGTH);
}

// A document is "stated" when the instruction refers to it — by @-mention or by
// name.
//
// THE @-MENTION MUST MATCH ON A PREFIX, AND THIS IS THE WHOLE POINT.
// The first version of this required the FULL filename: "@oneNDA" did not match
// "oneNDA_v2.1.docx", so the owner typed a perfectly reasonable instruction —
// "Revise @oneNDA to align with @CDA Playbook" — and was told his own document was
// unaccounted for. Nobody types "@oneNDA_v2.1.docx"; they type enough to identify
// the document and move on. Matching the full name only was a rule written from the
// implementation's convenience rather than from how the box is used.
//
// Prefix matching runs both ways: the token may be a prefix of the filename
// ("@oneNDA" → "oneNDA_v2.1.docx"), or the filename may be a prefix of the token,
// which covers a paste of the full name plus a stray character.
//
// A minimum of three characters keeps "@a" from matching everything.
export function statedInInstruction(instruction: string, name: string): boolean {
  if (!instruction || !name) return false;
  const full = String(name).toLowerCase();
  const stem = full.replace(/\.[a-z0-9]+$/, "");

  for (const token of mentionTokens(instruction)) {
    // The token may be a prefix of the filename ("@oneNDA" -> "oneNDA_v2.1.docx"),
    // or the filename may be a prefix of the token (a paste plus a stray character).
    //
    // THE SECOND DIRECTION NEEDS ITS OWN FLOOR. There was a three-character minimum
    // on the TOKEN and none on the STEM, so a file called "c.docx" — stem "c" — was
    // matched by the token "cda", and "on.docx" by "onenda". A document nobody
    // referred to was then treated as accounted for and SENT TO THE MODEL without
    // the author ever being asked what it was. That failed open, which is the one
    // direction this check must never fail.
    if (full.startsWith(token) || stem.startsWith(token)) return true;
    if (stem.length >= MIN_MATCH_LENGTH && token.startsWith(stem)) return true;
  }

  // Named in prose rather than @-mentioned — the owner asked for "by name or by
  // using an @-mention", so both count. Prose still requires the whole stem: a
  // partial word in prose is far more likely to be a coincidence than a reference.
  const haystack = instruction.toLowerCase();
  return haystack.includes(full) || (stem.length >= 4 && haystack.includes(stem));
}

export function unaccountedFor(attachments: DraftSource[], instruction: string): DraftSource[] {
  if (attachments.length <= 1) return [];

  // AN AMBIGUOUS MENTION ACCOUNTS FOR NOTHING.
  //
  // "@CDA" is a legitimate prefix of both "CDA Playbook.docx" and "CDAnything
  // Else.docx". Treating both as accounted for would send a document the author
  // never meant to include; picking one silently would be worse still. When a single
  // token matches more than one attachment, none of them is settled and the author is
  // asked — which is the entire purpose of this check, and the direction it must fail.
  const tokens = mentionTokens(instruction);
  const ambiguous = new Set<string>();
  for (const token of tokens) {
    const matched = attachments.filter((item) => statedInInstruction(`@${token}`, item.name));
    if (matched.length > 1) matched.forEach((item) => ambiguous.add(item.versionId));
  }

  return attachments.filter((item) => ambiguous.has(item.versionId) || !statedInInstruction(instruction, item.name));
}

const defaultTableSettings: ReviewTableSettings = {
  instructions: "",
  language: "English (United Kingdom)",
  dateFormat: "Default — as written",
  currencyFormat: "Default — as written",
  numberFormat: "Default — as written",
};

const sourceModeByCategory: Record<string, string> = {
  regional: "legal_sources",
  case: "legal_sources",
  web: "web_search",
  edgar: "web_search",
};

export interface WorkflowRunSubmitInput {
  files?: File[];
  file?: File;
  playbookId?: string;
  note?: string;
}

export interface WorkflowRunDeps {
  workflowKey: string;
  uploadFiles: (files: File[]) => Promise<any>;
  ensureProject: () => Promise<{ id: string }>;
  refreshProject: (projectId: string) => Promise<{ versions?: Array<{ version_id: string }> }>;
  post: (path: string, body: Record<string, unknown>) => Promise<{ ok: boolean; data?: any }>;
}

/**
 * Every document the user attached to the run, whichever shape the run screen
 * hands over. `files` is the current shape; `file` is the older single-document
 * one. Reading both keeps this correct on either side of that change instead of
 * silently running against one document because the other field was read.
 */
export function attachedWorkflowFiles(input: WorkflowRunSubmitInput): File[] {
  if (Array.isArray(input?.files) && input.files.length) return [...input.files];
  return input?.file ? [input.file] : [];
}

/** The version ids the upload itself reported, so no other document is picked up. */
export function uploadedVersionIds(uploadResult: any): string[] {
  const ids = ((uploadResult?.uploaded || []) as any[])
    .map((item) => item?.descriptor?.document_version_id || item?.document_version_id)
    .filter(Boolean) as string[];
  return [...new Set(ids)];
}

/**
 * Run a workflow against exactly what the user supplied: every attached
 * document, and the playbook they chose.
 *
 * Previously this uploaded only the first attachment and then sent the single
 * most recent version in the workspace, so a second document was silently
 * dropped and an unrelated document could be analysed in its place; the playbook
 * selection was never sent at all. The server refuses a run it cannot tie to a
 * playbook, and that refusal comes back as a normal result for the screen to
 * render.
 */
export async function submitWorkflowRun(deps: WorkflowRunDeps, input: WorkflowRunSubmitInput) {
  const files = attachedWorkflowFiles(input);
  if (!files.length) {
    throw new Error("Attach at least one document before running this workflow.");
  }
  const upload = await deps.uploadFiles(files);
  if (!upload) {
    throw new Error("The documents could not be uploaded, so the workflow was not run.");
  }
  const target = await deps.ensureProject();
  let documentVersionIds = uploadedVersionIds(upload);
  if (!documentVersionIds.length) {
    // The upload succeeded but reported no descriptors: fall back to the
    // documents this workspace just gained, never to a wider slice of it.
    const latest = await deps.refreshProject(target.id);
    documentVersionIds = (latest?.versions || [])
      .slice(-files.length)
      .map((version) => version.version_id)
      .filter(Boolean);
  }
  if (!documentVersionIds.length) {
    throw new Error("The attached documents could not be prepared for this run.");
  }
  const result = await deps.post(
    `/docint/workspaces/${encodeURIComponent(target.id)}/workflows/${encodeURIComponent(deps.workflowKey)}/run`,
    {
      document_version_ids: documentVersionIds,
      playbook: input.playbookId || undefined,
      instructions: input.note,
    }
  );
  if (!result.ok) throw new Error(result.data?.error || "The workflow could not be run.");
  return result.data;
}

export interface MappedCitationAnchor {
  documentId?: string;
  documentVersionId?: string;
  versionNumber?: number;
  versionLabel?: string;
  chunkId?: string;
  page?: number | string | null;
  paragraphIndex?: number;
  passage?: string;
  verificationStatus?: string;
}

export type MappedThreadCitation = ThreadCitation & MappedCitationAnchor;

function firstDefined<T>(...values: Array<T | null | undefined>): T | undefined {
  return values.find((value) => value !== undefined && value !== null) ?? undefined;
}

function citationMarker(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value.replace(/[^0-9]/g, ""), 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/**
 * Map a stored citation onto what the thread screen needs to resolve a marker to
 * the exact passage of the exact document version.
 *
 * Two stored shapes reach this point and they differ. `citations[]` carries the
 * quote and page inside `quotes[0]`; `evidence[]` carries a `snippet` and puts
 * the page in `metadata.page_number`. The earlier mapping built a fixed
 * six-field object, read `quote`, `snippet` and `page` off the top level of a
 * citation (where none of them exist) and dropped every anchor, which is why a
 * citation resolved to the whole document rather than the clause. Anchors are
 * carried through here; anything genuinely absent stays absent rather than being
 * filled in with a guess.
 */
export function mapThreadCitations(answer: DocumentIntelligenceMessage | undefined): MappedThreadCitation[] {
  const stored = (answer?.citations?.length ? answer.citations : answer?.evidence) || [];
  return (stored as any[]).map((item, index) => {
    const quote = (item?.quotes || [])[0] || null;
    const metadata = item?.metadata || {};
    const page = firstDefined<number | string>(item?.page, metadata.page_number, quote?.page_number);
    const paragraphIndex = typeof metadata.paragraph_index === "number" ? metadata.paragraph_index : undefined;
    const passage = firstDefined<string>(item?.quote, quote?.quote, item?.snippet);
    const documentVersionId = firstDefined<string>(item?.document_version_id, quote?.document_version_id);
    const chunkId = firstDefined<string>(item?.chunk_id, quote?.chunk_id);
    const locator = item?.sheet
      ? `${item.sheet}${item.range ? ` ${item.range}` : ""}`
      : page !== undefined && page !== ""
        ? `Page ${page}`
        : paragraphIndex !== undefined
          ? `Paragraph index ${paragraphIndex}`
          : "Document passage";
    return {
      id: firstDefined<string>(item?.citation_id, chunkId) || `citation-${index + 1}`,
      marker: citationMarker(item?.marker, index + 1),
      title: firstDefined<string>(item?.document_name, item?.filename, item?.document_label) || "Source document",
      type: item?.sheet ? "Spreadsheet" : "Document",
      locator,
      excerpt: passage,
      documentId: firstDefined<string>(item?.document_id, quote?.document_id),
      documentVersionId,
      versionNumber: typeof metadata.version_number === "number" ? metadata.version_number : undefined,
      versionLabel: firstDefined<string>(metadata.label),
      chunkId,
      page,
      paragraphIndex,
      passage,
      verificationStatus: firstDefined<string>(
        item?.verification_status,
        quote?.verification_status,
        item?.verified === true ? "verified" : undefined
      ),
    };
  });
}

/** The one Word export format, hoisted so its identity is stable across renders. */
const EXPORT_FORMATS = [{ id: "word", name: "Microsoft Word (.docx)" }];

function errorMessage(reason: unknown, fallback: string) {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

function insertMention(value: string, label: string) {
  const mention = `@${label}`;
  return /@$/.test(value)
    ? `${value.slice(0, -1)}${mention} `
    : `${value}${value && !/\s$/.test(value) ? " " : ""}${mention} `;
}

function sourceConfigurationSummary(selection: KnowledgeSourcesSelection, playbooks: DocumentIntelligencePlaybook[]) {
  const parts = selection.categoryIds.map((category) => {
    if (category === "playbooks") {
      const playbook = playbooks.find((item) => item.key === selection.playbookId);
      return playbook ? `Playbook: ${playbook.name} (apply to ${selection.applyPlaybookTo || "counterparty draft"})` : "Playbook";
    }
    if (category === "regional") {
      const scope = [
        selection.jurisdictions.length ? `jurisdictions ${selection.jurisdictions.join(", ")}` : "",
        selection.materialCategories.length ? `materials ${selection.materialCategories.join(", ")}` : "",
      ].filter(Boolean).join("; ");
      return `Regional knowledge${scope ? ` (${scope})` : ""}`;
    }
    if (category === "web") {
      return `Web Search${selection.includeDomains ? ` (include ${selection.includeDomains})` : ""}${selection.excludeDomains ? ` (exclude ${selection.excludeDomains})` : ""}`;
    }
    if (category === "case") return `US Case Law (${selection.courtScope || "all federal courts"})`;
    if (category === "edgar") return `EDGAR${selection.edgarFiler ? ` (${selection.edgarFiler})` : ""}`;
    return category;
  });
  return parts.join("; ");
}

function downloadUrl(path: string, fallbackName: string) {
  const token = localStorage.getItem("kontractai_token");
  return fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then(async (response) => {
      if (!response.ok) {
        // READ THE SERVER'S REASON. This used to throw a fixed sentence and
        // discard the body, so a refusal with a perfectly clear explanation —
        // an embargoed external copy, an expired artifact, a permission
        // failure — all reached the owner as the same eight words, and every
        // one of them had to be diagnosed from the logs instead of the screen.
        // The generic sentence stays as the fallback for a body we cannot read.
        let detail = "";
        try {
          const body = await response.clone().json();
          detail = body?.message || body?.error || "";
        } catch { /* not JSON — fall through to the generic sentence */ }
        throw new Error(detail || "The download could not be prepared.");
      }
      const disposition = response.headers.get("Content-Disposition") || "";
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1] || fallbackName;
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = filename.replace(/[<>:"/\\|?*\r\n]/g, "_");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
}

function mapReviewColumn(column: any, index: number): ReviewColumn {
  const formats: Record<string, ReviewColumn["type"]> = {
    verbatim: "Verbatim",
    summary: "Summary",
    date: "Date",
    currency: "Currency",
    boolean: "Yes / No",
    yes_no: "Yes / No",
  };
  return {
    id: String(column.key || column.id || `column_${index + 1}`),
    title: String(column.label || column.title || `Column ${index + 1}`),
    query: String(column.prompt || column.query || ""),
    type: formats[String(column.format || "").toLowerCase()] || "Summary",
    visible: column.visible !== false,
    order: index,
  };
}

function tableSettingsFromApi(value: any): ReviewTableSettings {
  return {
    instructions: String(value?.instructions || ""),
    language: String(value?.language || defaultTableSettings.language),
    dateFormat: String(value?.date_format || value?.dateFormat || defaultTableSettings.dateFormat),
    currencyFormat: String(value?.currency_format || value?.currencyFormat || defaultTableSettings.currencyFormat),
    numberFormat: String(value?.number_format || value?.numberFormat || defaultTableSettings.numberFormat),
  };
}

function tableSettingsToApi(value: ReviewTableSettings) {
  return {
    instructions: value.instructions,
    language: value.language,
    date_format: value.dateFormat,
    currency_format: value.currencyFormat,
    number_format: value.numberFormat,
  };
}

function mapTableRows(review: any, projectVersions: any[] = [], executionStatus?: string): ReviewRow[] {
  const versionNames = new Map(
    projectVersions.map((version) => [String(version.version_id), String(version.filename || "")])
  );
  const executionActive = ["pending", "running"].includes(String(executionStatus || ""));
  const terminalCellStatuses = new Set(["found", "not_found", "confirmed", "cleared", "escalated", "completed"]);
  return (review?.rows || []).map((row: any) => ({
    id: String(row.id),
    document: {
      id: String(row.document_version_id || row.id),
      name: String(
        versionNames.get(String(row.document_version_id))
        || (row.document_name === "Workspace document" ? "" : row.document_name)
        || "Document"
      ),
      origin: "project" as const,
    },
    cells: Object.fromEntries(Object.entries(row.cells || {}).map(([key, raw]: [string, any]) => [
      key,
      {
        value: raw.status === "needs_review" && !executionActive
          ? ""
          : raw.summary || raw.value || raw.note || "",
        clauseRef: raw.evidence?.[0]?.page ? `Page ${raw.evidence[0].page}` : raw.flag || "",
        status: raw.status === "failed"
          ? "failed"
          : executionActive && !terminalCellStatuses.has(raw.status)
            ? "generating"
            : "ready",
        citationId: raw.evidence?.[0]?.citation_id,
      },
    ])),
  }));
}

/**
 * Whether this thread still has work to do — and therefore whether it should be
 * attached to the live activity stream.
 *
 * The second arm is the legacy case and is the reason this is a named function rather
 * than an inline condition. Plan approval has been removed, but threads created before
 * that change can still be parked on the server with their user message in
 * `awaiting_approval`. There is no longer any control that would release one, so such a
 * thread must be treated as unfinished work to resume and narrate. Anything else leaves
 * the user looking at a request that will never move and no way to move it.
 */
export function threadRunIsUnfinished(thread: DocumentIntelligenceThread | null): boolean {
  const messages = thread?.messages || [];
  if (!messages.length) return false;
  const latestAssistant = [...messages].reverse().find((message) => message.role === "assistant");
  if (latestAssistant && ["pending", "running"].includes(latestAssistant.status || "")) return true;
  const latestUser = [...messages].reverse().find((message) => message.role === "user");
  return latestUser?.status === "awaiting_approval";
}

export function DocumentIntelligenceRedesign({ user }: { user?: User }) {
  const [route, setRoute] = useState(() => parseDocumentIntelligenceRoute());
  const [projects, setProjects] = useState<DocumentIntelligenceProject[]>([]);
  const [history, setHistory] = useState<DocumentIntelligenceHistoryItem[]>([]);
  const [playbooks, setPlaybooks] = useState<DocumentIntelligencePlaybook[]>([]);
  const [workflows, setWorkflows] = useState<DocumentIntelligenceWorkflow[]>([]);
  const [sourceModes, setSourceModes] = useState<DocumentIntelligenceSourceMode[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<any[]>([]);
  const [documentSources, setDocumentSources] = useState<any[]>([]);
  const [project, setProject] = useState<DocumentIntelligenceProject | null>(null);
  const [threads, setThreads] = useState<DocumentIntelligenceThread[]>([]);
  const [thread, setThread] = useState<DocumentIntelligenceThread | null>(null);
  const [table, setTable] = useState<any>(null);
  const [redline, setRedline] = useState<any>(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState<DocumentIntelligenceNotice | null>(null);
  // THE APPROVAL SURFACE for the rule-set gate (owner ruling Q-48). A draft
  // review refuses to run until an APPROVED reading of its playbook exists —
  // deliberately, with no compile-on-the-fly fallback. When the engine answers
  // rule_set_approval_required, this state opens a dialog that walks the
  // refusal's own instruction: compile the rule set, read what it compiled to,
  // approve it, then re-run the review. Null means the gate is not open.
  const [ruleSetGate, setRuleSetGate] = useState<{
    playbookVersionId: string | null;
    playbookName: string;
    instructions: string;
    ruleSet: any | null;
    rendering: string;
    diff: any | null;
    diffAgainst: any | null;
    note: string;
  } | null>(null);
  const [sourceSelection, setSourceSelection] = useState<KnowledgeSourcesSelection>({
    categoryIds: [],
    jurisdictions: [],
    materialCategories: [],
  });
  const [reviewDocuments, setReviewDocuments] = useState<ReviewDocument[]>([]);
  const [playbookPositions, setPlaybookPositions] = useState<PlaybookPosition[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<DocumentIntelligenceProject | null>(null);
  const [picker, setPicker] = useState<{ purpose: PickerPurpose; tab: DocumentIntelligenceSourceTab } | null>(null);
  const [assistantPrompt, setAssistantPrompt] = useState("");
  const [assistantDocuments, setAssistantDocuments] = useState<ContextDocument[]>([]);
  const [draftPrompt, setDraftPrompt] = useState("");
  // ONE list, not two. There used to be a single `draftSource` plus a separate
  // `draftContextSources`, which forced a positional rule — whichever file arrived
  // first became the document under review and everything else became anonymous
  // "context". The author could not say which was which, and a second upload
  // replaced the extras rather than adding to them.
  const [draftAttachments, setDraftAttachments] = useState<DraftSource[]>([]);
  const draftSource = draftAttachments.find((item) => item.role === "under_review") || draftAttachments[0] || null;
  const draftContextSources = draftAttachments.filter((item) => item.versionId !== draftSource?.versionId);
  const [draftPreview, setDraftPreview] = useState("");
  const [activity, setActivity] = useState<ActivityStreamState>(() => initialActivityState());
  const [activityConnection, setActivityConnection] = useState<ActivityConnection>("idle");
  // The DRAFT run's live narration, distinct from the assistant thread's. Null
  // when no draft review is running; the blank draft screen renders it while
  // the create call is in flight so the author watches stages, not a spinner.
  const [draftActivity, setDraftActivity] = useState<ActivityStreamState | null>(null);
  // WITHDRAWN 2026-08-05: the loaded draft no longer auto-hides the changes
  // panel for a just-created review. Owner: "any accept or reject of revisions
  // Must be displayed by default, not toggled." Decisions are on screen from
  // the first paint; expanding the document is the opt-in.
  // Default OFF, and off means the reasoning is never requested — not requested and hidden.
  const [showWorking, setShowWorking] = useState<boolean>(() => readShowWorking(user?.id));

  useEffect(() => {
    const update = () => {
      const nextRoute = parseDocumentIntelligenceRoute();
      if (nextRoute.screen === "assistant" && !nextRoute.workspaceId) {
        setAssistantPrompt("");
        setAssistantDocuments([]);
        setThread(null);
      }
      setRoute(nextRoute);
    };
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const refreshCatalog = useCallback(async () => {
    const results = await Promise.allSettled([
      documentIntelligenceApi.projects(),
      documentIntelligenceApi.history(),
      documentIntelligenceApi.playbooks(),
      documentIntelligenceApi.workflows(),
      documentIntelligenceApi.sourceModes(),
      documentIntelligenceApi.prompts(),
      documentIntelligenceApi.documentSources(),
    ]);
    if (results[0].status === "fulfilled") setProjects(results[0].value);
    if (results[1].status === "fulfilled") setHistory(results[1].value);
    if (results[2].status === "fulfilled") setPlaybooks(results[2].value);
    if (results[3].status === "fulfilled") setWorkflows(results[3].value);
    if (results[4].status === "fulfilled") setSourceModes(results[4].value);
    if (results[5].status === "fulfilled") setPromptTemplates(results[5].value);
    if (results[6].status === "fulfilled") setDocumentSources(results[6].value);
    const failure = results.find((result) => result.status === "rejected");
    if (failure?.status === "rejected") {
      setNotice({ kind: "warning", text: errorMessage(failure.reason, "Some Document Intelligence data could not be loaded.") });
    }
  }, []);

  useEffect(() => {
    setBusy("catalog");
    void documentIntelligenceApi.status()
      .then(refreshCatalog)
      .catch((reason) => setNotice({ kind: "error", text: errorMessage(reason, "Document Intelligence is unavailable.") }))
      .finally(() => setBusy(""));
  }, [refreshCatalog]);

  useEffect(() => {
    if (route.id || !["assistant", "draft", "table"].includes(route.screen)) return undefined;
    let current = true;
    const requested = route.workspaceId
      ? documentIntelligenceApi.project(route.workspaceId)
      : documentIntelligenceApi.activityWorkspace()
        .then((activity) => documentIntelligenceApi.project(activity.id));
    void requested
      .then((activity) => {
        if (!current) return;
        setProject(activity);
        setProjects((items) => [activity, ...items.filter((item) => item.id !== activity.id)]);
        if (route.screen === "assistant") {
          setAssistantPrompt("");
          setAssistantDocuments(route.workspaceId
            ? (activity.versions || []).map((version) => ({
              versionId: version.version_id,
              name: version.filename || "Project document",
              source: activity.name,
            }))
            : []);
        }
        if (route.screen === "table" && !route.id && route.workspaceId) {
          setReviewDocuments((activity.versions || []).map((version) => ({
            id: version.version_id,
            name: version.filename || "Project document",
            origin: "project" as const,
          })));
        }
      })
      .catch((reason) => {
        if (current) {
          setNotice({ kind: "warning", text: errorMessage(reason, "Your recent Document Intelligence files could not be restored.") });
        }
      });
    return () => {
      current = false;
    };
  }, [route.id, route.screen, route.workspaceId]);

  useEffect(() => {
    if (route.screen !== "project" || !route.id) return;
    setBusy("project");
    void Promise.all([
      documentIntelligenceApi.project(route.id),
      documentIntelligenceApi.threads(route.id),
    ]).then(([nextProject, nextThreads]) => {
      setProject(nextProject);
      setThreads(nextThreads);
    }).catch((reason) => {
      setNotice({ kind: "error", text: errorMessage(reason, "The project could not be opened.") });
    }).finally(() => setBusy(""));
  }, [route.id, route.screen]);

  const refreshThread = useCallback((threadId: string) => {
    void documentIntelligenceApi.thread(threadId)
      .then(setThread)
      .catch((reason) => setNotice({
        kind: "warning",
        text: errorMessage(reason, "Assistant progress could not be refreshed."),
      }));
  }, []);

  // The run is unfinished — including the legacy awaiting-approval case, which now
  // resumes rather than waiting for a control that no longer exists.
  const threadIsRunning = route.screen === "thread" && Boolean(route.id) && threadRunIsUnfinished(thread);

  // The poll is what materialises the finished ANSWER. The stream below is what narrates
  // the work. They are deliberately separate: a dropped stream must not cost the answer.
  useEffect(() => {
    if (!threadIsRunning || !route.id) return undefined;
    const threadId = route.id;
    const timer = window.setInterval(() => refreshThread(threadId), 1200);
    return () => window.clearInterval(timer);
  }, [refreshThread, route.id, threadIsRunning, thread?.revision]);

  // The preference is per user, so a late-arriving user prop re-reads it rather than
  // leaving the previous account's setting on screen.
  useEffect(() => setShowWorking(readShowWorking(user?.id)), [user?.id]);

  const changeShowWorking = useCallback((next: boolean) => {
    setShowWorking(next);
    writeShowWorking(user?.id, next);
    // Turning it OFF drops what has already been shown. Raw working-out is not part of
    // the record of this request and must not linger once the user has said to hide it.
    if (!next) setActivity((current) => (current.reasoning.length ? { ...current, reasoning: [] } : current));
  }, [user?.id]);

  // A different thread is a different run: its narration starts empty rather than
  // inheriting the previous thread's.
  useEffect(() => {
    setActivity(initialActivityState());
    setActivityConnection("idle");
  }, [route.id]);

  // The live activity stream.
  //
  // `threadIsRunning` and `showWorking` are booleans, so this effect re-runs only when
  // one of them actually flips — not on every poll. Toggling "show working" reconnects
  // (the server only sends reasoning when it was asked at connect time) and deliberately
  // KEEPS the narration accumulated so far; only a new run clears it.
  useEffect(() => {
    if (!threadIsRunning || !route.id) return undefined;
    const threadId = route.id;
    const controller = new AbortController();
    setActivityConnection("connecting");
    setActivity((current) => (current.terminal ? initialActivityState() : current));
    void openActivityStream({
      threadId,
      showWorking,
      signal: controller.signal,
      onOpen: () => setActivityConnection("open"),
      onEvent: (event) => {
        setActivity((current) => reduceActivityEvent(current, event));
        // The stream reports that the run ended; the thread read is what carries the
        // answer text and citations, so ask for it immediately rather than waiting a tick.
        if (event.type === "done" || event.type === "error") refreshThread(threadId);
      },
      onClose: (close) => {
        setActivityConnection("closed");
        if (!close.ok && close.message) setNotice({ kind: "warning", text: close.message });
      },
    });
    return () => controller.abort();
  }, [refreshThread, route.id, showWorking, threadIsRunning]);

  useEffect(() => {
    if (route.screen !== "thread" || !route.id) return;
    setBusy("thread");
    void documentIntelligenceApi.thread(route.id)
      .then(setThread)
      .catch((reason) => setNotice({ kind: "error", text: errorMessage(reason, "The thread could not be opened.") }))
      .finally(() => setBusy(""));
  }, [route.id, route.screen]);

  useEffect(() => {
    if (route.screen !== "thread" || !thread?.workspace_id || project?.id === thread.workspace_id) return;
    void documentIntelligenceApi.project(thread.workspace_id)
      .then(setProject)
      .catch((reason) => setNotice({ kind: "warning", text: errorMessage(reason, "The thread's source files could not be restored.") }));
  }, [project?.id, route.screen, thread?.workspace_id]);

  // Restores a thread's stored source set when the thread is OPENED. It must not run
  // again while the thread stays open: `project` is a fresh object after every
  // refreshProject, and re-running here replaced the user's in-session file strip —
  // and therefore the document_version_ids sent with the next follow-up — with the
  // documents the thread started with. The key is thread + project id, so a late or
  // corrected workspace load still hydrates, but a plain refresh of the same
  // workspace does not.
  const hydratedThreadSourcesRef = useRef<string | null>(null);
  useEffect(() => {
    if (route.screen !== "thread") {
      hydratedThreadSourcesRef.current = null;
      return;
    }
    if (!thread || !project) return;
    const hydrationKey = `${thread.id}:${project.id}`;
    if (hydratedThreadSourcesRef.current === hydrationKey) return;
    hydratedThreadSourcesRef.current = hydrationKey;
    const versionIds = [...(thread.messages || [])]
      .reverse()
      .find((message) => message.role === "user")
      ?.source_set?.document_version_ids || [];
    const byId = new Map((project.versions || []).map((version) => [version.version_id, version]));
    setAssistantDocuments(versionIds.map((versionId) => ({
      versionId,
      name: byId.get(versionId)?.filename || "Selected document",
      source: project.name,
    })));
  }, [project, route.screen, thread?.id]);

  useEffect(() => {
    if (route.screen !== "table" || !route.id) {
      if (route.screen === "table") setTable(null);
      return;
    }
    setBusy("table");
    void Promise.all([
      documentIntelligenceApi.table(route.id),
      documentIntelligenceApi.tableExecution(route.id),
    ])
      .then(([nextTable, execution]) => setTable({ ...nextTable, execution }))
      .catch((reason) => setNotice({ kind: "error", text: errorMessage(reason, "The review table could not be opened.") }))
      .finally(() => setBusy(""));
  }, [route.id, route.screen]);

  useEffect(() => {
    const workspaceId = table?.workspace_id;
    if (route.screen !== "table" || !route.id || !workspaceId || project?.id === workspaceId) return;
    void documentIntelligenceApi.project(workspaceId)
      .then(setProject)
      .catch((reason) => setNotice({ kind: "warning", text: errorMessage(reason, "The table's project files could not be restored.") }));
  }, [project?.id, route.id, route.screen, table?.workspace_id]);

  useEffect(() => {
    if (route.screen !== "table" || !route.id || !["pending", "running"].includes(table?.execution?.status)) {
      return undefined;
    }
    const refresh = () => {
      void Promise.all([
        documentIntelligenceApi.table(route.id!),
        documentIntelligenceApi.tableExecution(route.id!),
      ]).then(([nextTable, execution]) => setTable({ ...nextTable, execution }))
        .catch((reason) => setNotice({ kind: "warning", text: errorMessage(reason, "Review progress could not be refreshed.") }));
    };
    const timer = window.setInterval(refresh, 1500);
    return () => window.clearInterval(timer);
  }, [route.id, route.screen, table?.execution?.status]);

  useEffect(() => {
    if (route.screen !== "draft" || !route.id) {
      if (route.screen === "draft") setRedline(null);
      return;
    }
    setBusy("draft");
    // TWO ENGINES, ONE SCREEN. New reviews come from the rebuilt engine; reviews
    // created before the switch belong to the original one, and its rows are in
    // a different table. Ask the rebuilt engine first and fall back — an id is
    // only ever valid in one of them, so this cannot resolve to the wrong review.
    void documentIntelligenceApi.redlineV2Review(route.id)
      .then((review) => setRedline({ ...review, engine: "v2" }))
      .catch(() => documentIntelligenceApi.redline(route.id!).then((review) => setRedline({ ...review, engine: "v1" })))
      .catch((reason) => setNotice({ kind: "error", text: errorMessage(reason, "The draft could not be opened.") }))
      .finally(() => setBusy(""));
  }, [route.id, route.screen]);

  useEffect(() => {
    const workspaceId = redline?.review?.workspace_id;
    if (route.screen !== "draft" || !route.id || !workspaceId || project?.id === workspaceId) return;
    void documentIntelligenceApi.project(workspaceId)
      .then(setProject)
      .catch((reason) => setNotice({ kind: "warning", text: errorMessage(reason, "The draft's project files could not be restored.") }));
  }, [project?.id, redline?.review?.workspace_id, route.id, route.screen]);

  const ensureProject = useCallback(async () => {
    if (project?.id) return project;
    const activityWorkspace = await documentIntelligenceApi.activityWorkspace();
    const activity = await documentIntelligenceApi.project(activityWorkspace.id);
    setProject(activity);
    setProjects((items) => [activity, ...items.filter((item) => item.id !== activity.id)]);
    return activity;
  }, [project]);

  const refreshProject = useCallback(async (projectId: string) => {
    const detail = await documentIntelligenceApi.project(projectId);
    setProject(detail);
    setProjects((items) => items.map((item) => item.id === detail.id ? detail : item));
    return detail;
  }, []);

  const removeProjectVersion = useCallback(async (versionId: string, label: string) => {
    if (!project?.id) return;
    setBusy("remove-version");
    try {
      await documentIntelligenceApi.removeVersion(project.id, versionId);
      await refreshProject(project.id);
      // Removing the document under review must not silently promote another one into
      // its place. The list keeps its order and roles; if the removed item WAS the one
      // under review, the next submit re-derives the answer from the instruction, or
      // asks. Promotion by position is the rule this change exists to delete.
      setDraftAttachments((current) => current.filter((item) => item.versionId !== versionId));
      if (draftSource?.versionId === versionId) setDraftPreview("");
      setReviewDocuments((current) => current.filter((item) => item.id !== versionId));
      setNotice({ kind: "success", text: `${label} removed from this Document Intelligence workspace.` });
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The document could not be removed.") });
    } finally {
      setBusy("");
    }
  }, [draftSource?.versionId, project?.id, refreshProject]);

  const uploadToProject = useCallback(async (files: File[]) => {
    if (!files.length) return;
    setBusy("upload");
    try {
      const target = await ensureProject();
      const result = await documentIntelligenceApi.uploadFiles(target.id, files.slice(0, 50));
      const jobs = (result?.uploaded || []).map((item: any) => item?.job).filter((job: any) => job?.id);
      if (jobs.length) {
        setNotice({ kind: "info", text: `Processing ${jobs.length} uploaded document${jobs.length === 1 ? "" : "s"} for search and review…` });
        const deadline = Date.now() + 60_000;
        let pending = jobs;
        while (pending.length && Date.now() < deadline) {
          await new Promise((resolve) => window.setTimeout(resolve, 700));
          const statuses = await Promise.all(pending.map((job: any) => documentIntelligenceApi.processingJob(job.id)));
          const failed = statuses.find((job: any) => ["failed", "cancelled"].includes(job.status));
          if (failed) throw new Error(`Document processing failed${failed.error_code ? ` (${failed.error_code})` : ""}.`);
          pending = statuses.filter((job: any) => job.status !== "succeeded");
        }
        if (pending.length) throw new Error("The upload finished, but document processing did not complete in time. Try again in a moment.");
      }
      const detail = await refreshProject(target.id);
      void documentIntelligenceApi.documentSources().then(setDocumentSources);
      if (route.screen === "table" && !route.id) {
        const uploadedVersionIds = new Set(
          (result?.uploaded || [])
            .map((item: any) => item?.descriptor?.document_version_id || item?.document_version_id)
            .filter(Boolean)
        );
        setReviewDocuments((items) => {
          const known = new Set(items.map((item) => item.id));
          const uploaded = (detail.versions || [])
            .filter((version) => uploadedVersionIds.has(version.version_id) && !known.has(version.version_id))
            .map((version) => ({
              id: version.version_id,
              name: version.filename || "Uploaded document",
              origin: "upload" as const,
            }));
          return [...items, ...uploaded].slice(0, 50);
        });
      }
      const uploadedCount = result?.uploaded?.length || files.length;
      const rejectedCount = result?.rejected?.length || 0;
      setNotice({
        kind: rejectedCount ? "warning" : "success",
        text: rejectedCount
          ? `${uploadedCount} file${uploadedCount === 1 ? "" : "s"} added; ${rejectedCount} rejected: ${result.rejected[0]?.error || "check the file type and size"}.`
          : `${uploadedCount} file${uploadedCount === 1 ? "" : "s"} added to the project.`,
      });
      return result;
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The files could not be uploaded.") });
      return undefined;
    } finally {
      setBusy("");
    }
  }, [ensureProject, refreshProject, route.id, route.screen]);

  const addVersionsToLoadedTable = useCallback(async (versionIds: string[]) => {
    if (route.screen !== "table" || !route.id || !table || !versionIds.length) return;
    const existingIds = (table.rows || [])
      .map((row: any) => row?.document_version_id)
      .filter(Boolean);
    const documentVersionIds = [...new Set([...existingIds, ...versionIds])].slice(0, 50);
    const updated = await documentIntelligenceApi.updateTable(table.id, table.revision, {
      document_version_ids: documentVersionIds,
    });
    setTable(updated);
  }, [route.id, route.screen, table]);

  const uploadReviewFiles = useCallback(async (files: File[]) => {
    const result = await uploadToProject(files);
    const versionIds = (result?.uploaded || [])
      .map((item: any) => item?.descriptor?.document_version_id || item?.document_version_id)
      .filter(Boolean);
    if (versionIds.length) {
      try {
        await addVersionsToLoadedTable(versionIds);
      } catch (reason) {
        setNotice({ kind: "error", text: errorMessage(reason, "The files uploaded, but could not be added to this review table.") });
      }
    }
    return result;
  }, [addVersionsToLoadedTable, uploadToProject]);

  const uploadAssistantFiles = useCallback(async (files: File[]) => {
    const result = await uploadToProject(files);
    const additions = (result?.uploaded || []).map((item: any, index: number) => ({
      versionId: item?.descriptor?.document_version_id || item?.document_version_id,
      name: item?.descriptor?.file_name || files[index]?.name || "Uploaded document",
      source: "Uploaded from this computer",
    })).filter((item: DraftSource) => Boolean(item.versionId));
    setAssistantDocuments((current) => {
      const next = new Map(current.map((item) => [item.versionId, item]));
      additions.forEach((item: DraftSource) => next.set(item.versionId, item));
      return [...next.values()].slice(0, 50);
    });
    return result;
  }, [uploadToProject]);

  const activeSourceModes = useMemo(() => {
    const modes = sourceSelection.categoryIds
      .map((category) => sourceModeByCategory[category])
      .filter((value): value is string => Boolean(value));
    return [...new Set(modes)].slice(-2);
  }, [sourceSelection.categoryIds]);

  const sourceSummary = useMemo(
    () => sourceConfigurationSummary(sourceSelection, playbooks),
    [playbooks, sourceSelection]
  );

  const prompts: CorePrompt[] = useMemo(() => promptTemplates.map((item) => ({
    id: String(item.id),
    title: String(item.name || "Saved prompt"),
    prompt: String(item.prompt_text || ""),
    meta: String(item.category || "Custom"),
  })), [promptTemplates]);

  const mentions: CoreMention[] = useMemo(() => [
    ...(project?.versions || []).map((version) => ({
      id: `file:${version.version_id}`,
      label: version.filename || "Project document",
      type: "File",
      detail: `Project file · v${version.version_number || 1}`,
    })),
    ...sourceSelection.categoryIds.map((category) => ({
      id: `source:${category}`,
      label: category === "case" ? "US Case Law" : category === "web" ? "Web Search" : category === "regional" ? "Regional knowledge" : category === "edgar" ? "EDGAR" : "Playbook",
      type: "Source",
      detail: sourceSummary,
    })),
    ...workflows.slice(0, 20).map((workflow) => ({
      id: `workflow:${workflow.key}`,
      label: workflow.name,
      type: "Workflow",
      detail: workflow.description,
    })),
  ], [project?.versions, sourceSelection.categoryIds, sourceSummary, workflows]);

  const pickerItems = useMemo<DocumentIntelligenceSourceItem[]>(() => {
    const projectNameById = new Map(projects.map((item) => [item.id, item.name]));
    const unique = new Map<string, any>();
    documentSources.forEach((source) => {
      if (source.document_version_id && !unique.has(source.document_version_id)) {
        unique.set(source.document_version_id, source);
      }
    });
    return [...unique.values()].map((source) => {
      const tab: DocumentIntelligenceSourceTab = source.contract_id
        ? "repository"
        : source.request_id
          ? "library"
          : source.workspace_id
            ? "projects"
            : "recent";
      const origin = source.contract_id
        ? "Contract Repository"
        : source.request_id
          ? "Request record"
          : source.workspace_id
            ? projectNameById.get(source.workspace_id) || "Document Intelligence project"
            : "Recent upload";
      return {
        id: `${tab}:${source.document_version_id}`,
        name: source.file_name || "Document",
        tab,
        type: source.classification || source.mime_type || "Document",
        source: origin,
        workspaceId: source.workspace_id,
        versionId: source.document_version_id,
        detail: `Version ${source.version_number || 1} · ${source.processing_status || "available"}`,
        disabled: source.storage_state && source.storage_state !== "available",
        disabledReason: source.storage_state && source.storage_state !== "available"
          ? "This document is not currently available."
          : undefined,
      };
    });
  }, [documentSources, projects]);

  const pickerAvailability = useMemo<Record<DocumentIntelligenceSourceTab, DocumentIntelligenceSourceAvailability>>(() => ({
    recent: { status: "available" },
    library: { status: "available" },
    projects: { status: "available" },
    templates: { status: "available" },
    generated: { status: "available" },
    "review-tables": { status: "available" },
    repository: { status: "available" },
    sharepoint: { status: "unavailable", message: "SharePoint is not configured for this account." },
  }), []);

  const knowledgeAvailability = useMemo(() => {
    const web = sourceModes.find((mode) => mode.key === "web_search");
    const legal = sourceModes.find((mode) => mode.key === "legal_sources");
    const sourceState = (mode: DocumentIntelligenceSourceMode | undefined, fallback: string) => ({
      status: mode?.status || "unconfigured",
      message: mode?.status === "available" ? mode.detail : mode?.detail || fallback,
    });
    return {
      playbooks: playbooks.length
        ? { status: "available" }
        : { status: "unconfigured", message: "No published playbooks are available." },
      regional: sourceState(legal, "Regional legal sources are not configured for this workspace."),
      case: sourceState(legal, "US Case Law is not configured for this workspace."),
      web: sourceState(web, "Web Search is not configured for this workspace."),
      edgar: { status: "unconfigured", message: "EDGAR search is not configured for this workspace." },
    };
  }, [playbooks.length, sourceModes]);

  const improvePrompt = useCallback(async (prompt: string) => {
    try {
      return await documentIntelligenceApi.improvePrompt(
        prompt,
        [
          sourceSummary ? `Selected sources: ${sourceSummary}` : "",
          project?.versions?.length ? `Attached files: ${project.versions.map((version) => version.filename).filter(Boolean).join(", ")}` : "",
        ].filter(Boolean).join(". ")
      );
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The prompt could not be improved.") });
      return null;
    }
  }, [project?.versions, sourceSummary]);

  const savePrompt = useCallback(async (prompt: string, title?: string) => {
    try {
      const saved = await documentIntelligenceApi.savePrompt({
        name: title || `Saved prompt ${new Date().toLocaleDateString()}`,
        prompt_text: prompt,
        category: "Custom",
      });
      setPromptTemplates((items) => [...items.filter((item) => item.id !== saved.id), saved]);
      setNotice({ kind: "success", text: `Prompt “${saved.name}” saved to the Library.` });
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The prompt could not be saved.") });
    }
  }, []);

  const usePickedDocuments = useCallback(async (items: DocumentIntelligenceSourceItem[]) => {
    if (!picker || !items.length) return;
    setBusy("source-picker");
    try {
      const target = await ensureProject();
      const selected = items.filter((item) => item.versionId);
      for (const item of selected) {
        await documentIntelligenceApi.addVersion(target.id, item.versionId!);
      }
      await refreshProject(target.id);
      if (picker.purpose === "draft") {
        const sources: DraftSource[] = selected.map((item) => ({
          versionId: item.versionId!,
          name: item.name,
          source: item.source,
          role: "unstated" as AttachmentRole,
        }));
        if (sources.length) {
          setDraftAttachments((current) => {
            const seen = new Set(current.map((entry) => entry.versionId));
            const merged = [...current, ...sources.filter((entry) => !seen.has(entry.versionId))];
            return merged.map((entry, index) => (
              index === 0 && entry.role === "unstated" ? { ...entry, role: "under_review" as AttachmentRole } : entry
            ));
          });
          const preview = await documentIntelligenceApi.documentPreview(target.id, sources[0].versionId);
          setDraftPreview((current) => current || String(preview?.text || ""));
        }
      } else if (picker.purpose === "review") {
        if (route.screen === "table" && route.id) {
          await addVersionsToLoadedTable(selected.map((item) => item.versionId!));
        } else {
          setReviewDocuments((current) => {
            const next = new Map(current.map((item) => [item.id, item]));
            selected.forEach((item) => next.set(item.versionId!, {
              id: item.versionId!,
              name: item.name,
              origin: item.tab === "repository" ? "repository" : "project",
            }));
            return [...next.values()].slice(0, 50);
          });
        }
      } else if (picker.purpose === "assistant") {
        setAssistantDocuments((current) => {
          const next = new Map(current.map((item) => [item.versionId, item]));
          selected.forEach((item) => next.set(item.versionId!, {
            versionId: item.versionId!,
            name: item.name,
            source: item.source,
          }));
          return [...next.values()].slice(0, 50);
        });
      }
      setPicker(null);
      setNotice({
        kind: "success",
        text: picker.purpose === "draft"
          ? `Draft selected from ${selected[0]?.source || "KontractAI"}.`
          : `${selected.length} existing document${selected.length === 1 ? "" : "s"} added.`,
      });
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The selected documents could not be added.") });
    } finally {
      setBusy("");
    }
  }, [addVersionsToLoadedTable, ensureProject, picker, refreshProject, route.id, route.screen]);

  const importDraft = useCallback(async (file: File) => {
    const result = await uploadToProject([file]);
    const descriptor = result?.uploaded?.[0]?.descriptor;
    if (descriptor?.document_version_id) {
      setDraftAttachments((current) => {
        if (current.some((item) => item.versionId === descriptor.document_version_id)) return current;
        const merged: DraftSource[] = [...current, {
          versionId: descriptor.document_version_id,
          name: descriptor.file_name || file.name,
          source: "Uploaded from this computer",
          role: "unstated" as AttachmentRole,
        }];
        return merged.map((item, index) => (
          index === 0 && item.role === "unstated" ? { ...item, role: "under_review" as AttachmentRole } : item
        ));
      });
      const target = await ensureProject();
      const preview = await documentIntelligenceApi.documentPreview(target.id, descriptor.document_version_id);
      setDraftPreview(String(preview?.text || ""));
      setNotice({ kind: "success", text: `${descriptor.file_name || file.name} is ready. Add instructions and select Send.` });
    }
  }, [ensureProject, uploadToProject]);

  const uploadDraftFiles = useCallback(async (files: File[]) => {
    if (!files.length) return;
    const result = await uploadToProject(files);
    const sources: DraftSource[] = (result?.uploaded || []).map((item: any, index: number) => ({
      versionId: item?.descriptor?.document_version_id,
      name: item?.descriptor?.file_name || files[index]?.name || "Uploaded document",
      source: "Uploaded from this computer",
      role: "unstated" as AttachmentRole,
    })).filter((item: DraftSource) => Boolean(item.versionId));
    if (!sources.length) return;

    // APPEND, never replace. This used to keep sources[0] and discard whatever was
    // already attached, so a second upload silently dropped the first batch. The
    // author asked for both documents; they get both documents.
    setDraftAttachments((current) => {
      const seen = new Set(current.map((item) => item.versionId));
      const merged = [...current, ...sources.filter((item) => !seen.has(item.versionId))];
      // The FIRST attachment overall is provisionally the one under review, because a
      // lone document is unambiguous. It is a default, not a rule: as soon as a second
      // document arrives, the instruction decides and this is re-evaluated on submit.
      return merged.map((item, index) => (
        index === 0 && item.role === "unstated" ? { ...item, role: "under_review" as AttachmentRole } : item
      ));
    });

    const target = await ensureProject();
    const preview = await documentIntelligenceApi.documentPreview(target.id, sources[0].versionId);
    setDraftPreview((current) => current || String(preview?.text || ""));
  }, [ensureProject, uploadToProject]);

  const submitAssistant = useCallback(async (prompt: string, existingThreadId?: string) => {
    setBusy("assistant");
    try {
      const target = await ensureProject();
      const nextThread = existingThreadId
        ? await documentIntelligenceApi.thread(existingThreadId)
        : await documentIntelligenceApi.createThread(target.id, prompt.slice(0, 120));
      const previousDocumentIds = ([...(nextThread.messages || [])]
        .reverse()
        .find((message) => message.role === "user")
        ?.source_set?.document_version_ids) || [];
      const documentIds = assistantDocuments.length
        ? assistantDocuments.map((document) => document.versionId)
        : existingThreadId ? previousDocumentIds : [];
      const result = await documentIntelligenceApi.sendMessage(nextThread.id, {
        content: prompt,
        document_version_ids: documentIds,
        source_modes: activeSourceModes.length ? activeSourceModes : ["selected_documents"],
        source_configuration: sourceSelection,
        // There is no plan mode. A question starts the work; the activity panel narrates it.
        // Sent explicitly rather than omitted so the intent is unambiguous against a server
        // that still understands the flag.
        plan_only: false,
      });
      if (result?.thread) setThread(result.thread);
      setAssistantPrompt("");
      navigateDocumentIntelligence({ screen: "thread", id: nextThread.id });
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The request could not be sent.") });
    } finally {
      setBusy("");
    }
  }, [activeSourceModes, assistantDocuments, ensureProject, sourceSelection]);

  const submitDraft = useCallback(async (instructions: string) => {
    if (!draftSource) {
      await submitAssistant(instructions);
      return;
    }
    if (/^(revise|review|redline|improve|fix|edit)\.?$/i.test(instructions.trim())) {
      setNotice({ kind: "warning", text: "Tell Document Intelligence what to revise—for example, the clause, fallback position, or playbook standard to apply." });
      return;
    }

    // ASK RATHER THAN GUESS.
    //
    // NO @-GATE. Owner-directed 2026-08-05: "I also don't want to have to put in
    // '@'. If a document that I've uploaded is self-explanatory, let the LLM come
    // back and request clarification, not you as a gatekeeper."
    //
    // This used to refuse the run until every attachment was named in the
    // instruction. The reasoning was that an unreferenced attachment is a
    // document whose purpose cannot be determined — but that reasoning put the
    // judgement in the wrong place. Whether a CDA and a playbook need explaining
    // is a question the model can answer from the documents themselves, and when
    // it genuinely cannot, it is the one that should say so. A screen that
    // refuses to start is a gatekeeper standing in front of a question it is not
    // equipped to answer.
    //
    // @-mentions still WORK and still order the documents (below). They are no
    // longer required.

    // The document UNDER REVIEW is the one the instruction names first — not the one
    // that happened to be uploaded first. Everything else attached is reference
    // material. With a single attachment there is nothing to decide.
    const ordered = draftAttachments.length > 1
      ? [...draftAttachments].sort((a, b) => instructions.toLowerCase().indexOf(a.name.toLowerCase().replace(/\.[a-z0-9]+$/, ""))
        - instructions.toLowerCase().indexOf(b.name.toLowerCase().replace(/\.[a-z0-9]+$/, "")))
      : draftAttachments;
    const primary = ordered[0] || draftSource;
    const references = ordered.filter((item) => item.versionId !== primary.versionId);
    // Named OUTSIDE the try: the rule-set gate in the catch needs to know which
    // attached document was the playbook, so the approval dialog can compile it.
    const playbookAttachment = references[0] || null;

    // THE MODE IS STATED BEFORE THE RUN (two-mode spec §2). With no reference
    // attached, the server will run a GENERAL review — a memo of market-practice
    // observations, not your firm's positions and not a redline — and the
    // author is told so here, before any work starts, not discovered after.
    if (references.length === 0) {
      setNotice({
        kind: "warning",
        text: "No playbook attached — this will be a general review. Observations will reflect market practice as the model understands it, not your firm's positions, and nothing will be applied to the document.",
      });
    }

    setBusy("draft-create");
    // Watch the run while it works. The token is generated here, sent with the
    // create call, and used to attach to the run's progress stream — so the
    // screen narrates stages ("Reading the document…", "Reviewing section 2 of
    // 5…") instead of holding a silent spinner for the whole review.
    const progressToken = crypto.randomUUID();
    setDraftActivity(initialActivityState());
    const streamAbort = new AbortController();
    try {
      const target = await ensureProject();
      // Send the create call FIRST, then attach the stream while it is in
      // flight. The server opens the run before doing any work and buffers
      // every event, so a stream that attaches a moment later replays the whole
      // narrative — this ordering removes the race entirely.
      // THE REBUILT ENGINE. Owner-approved 2026-08-05: the Draft screen drives
      // the engine we rebuilt and measured, not the original one. The first
      // reference document named in the instruction is the playbook; with no
      // reference attached, the typed instruction IS the standard being applied,
      // which is honest — nothing else was supplied to review against.
      const createPromise = documentIntelligenceApi.redlineV2Analyze({
        version_id: primary.versionId,
        ...(playbookAttachment ? { playbook_version_id: playbookAttachment.versionId } : {}),
        request_id: null,
        context: instructions,
        progress_token: progressToken,
      } as any);
      void openActivityStream({
        threadId: "",
        showWorking: false,
        path: redlineProgressStreamPath(progressToken),
        signal: streamAbort.signal,
        onEvent: (event) => setDraftActivity((current) => reduceActivityEvent(current || initialActivityState(), event)),
        // Stream failure is DISPLAY failure only: the create call is the work,
        // and it succeeds or fails on its own. The result still lands when the
        // call returns.
        onClose: () => undefined,
      });
      const result = await createPromise;
      const reviewId = result?.review?.id || result?.id;
      if (!reviewId) throw new Error("The draft review did not return an identifier.");
      setDraftPrompt("");
      await refreshCatalog();
      navigateDocumentIntelligence({ screen: "draft", id: reviewId });
    } catch (reason) {
      if ((reason as any)?.code === "rule_set_approval_required") {
        // The gate working, not a failure. The engine refuses to review against
        // a playbook nobody has approved a reading of (Q-48); instead of an
        // error banner, open the surface that does what the refusal asks —
        // compile, read, approve — and remember the instructions so the review
        // re-runs the moment approval lands.
        setRuleSetGate({
          playbookVersionId: playbookAttachment?.versionId || null,
          playbookName: String((reason as any)?.details?.playbook_name || playbookAttachment?.name || "the attached playbook"),
          instructions,
          ruleSet: null,
          rendering: "",
          diff: null,
          diffAgainst: null,
          note: "",
        });
      } else {
        setNotice({ kind: "error", text: errorMessage(reason, "The draft review could not be started.") });
      }
    } finally {
      streamAbort.abort();
      setDraftActivity(null);
      setBusy("");
    }
  }, [draftAttachments, draftSource, ensureProject, refreshCatalog, submitAssistant]);

  // Step one of the gate: compile the playbook into its rule set and show the
  // owner-readable rendering. Nothing is approved here — compiling only
  // produces the draft reading that approval then freezes.
  const compileGateRuleSet = useCallback(async () => {
    if (!ruleSetGate) return;
    if (!ruleSetGate.playbookVersionId) {
      setNotice({ kind: "error", text: "The playbook document for this review could not be identified, so its rule set cannot be compiled from here." });
      return;
    }
    setBusy("rule-set-compile");
    try {
      const result = await documentIntelligenceApi.redlineV2RuleSetCompile({
        playbook_version_id: ruleSetGate.playbookVersionId,
      });
      setRuleSetGate((gate) => gate ? {
        ...gate,
        ruleSet: result.rule_set,
        rendering: String(result.rendering || ""),
        diff: result.diff || null,
        diffAgainst: result.diff_against || null,
      } : gate);
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The playbook's rule set could not be compiled.") });
    } finally {
      setBusy("");
    }
  }, [ruleSetGate]);

  // Step two: approve the compiled reading, then re-run the review that was
  // refused — with the same instructions the author already typed.
  const approveGateRuleSet = useCallback(async () => {
    if (!ruleSetGate?.ruleSet) return;
    setBusy("rule-set-approve");
    const { instructions, note, ruleSet } = ruleSetGate;
    try {
      await documentIntelligenceApi.redlineV2RuleSetApprove(ruleSet.id, note.trim() || undefined);
      setRuleSetGate(null);
      setNotice({ kind: "success", text: `Rule set approved for ${ruleSetGate.playbookName}. Starting the review.` });
      setBusy("");
      await submitDraft(instructions);
    } catch (reason) {
      setNotice({ kind: "error", text: errorMessage(reason, "The rule set could not be approved.") });
      setBusy("");
    }
  }, [ruleSetGate, submitDraft]);

  const playbookRows: PlaybookListItem[] = playbooks.map((item) => ({
    id: item.key,
    title: item.name,
    practiceArea: item.practice_area,
    description: item.description,
    modifiedAt: "Workspace catalogue",
    ruleCount: item.positions || item.topics?.length || 0,
    sharedWith: "Workspace",
    createdBy: "Workspace",
    status: item.status || "published",
  }));

  const sourceItems: SourceLibraryItem[] = [
    ...prompts.map((item) => ({
      id: item.id,
      tab: "prompts" as const,
      kicker: item.meta || "Prompt",
      title: item.title,
      description: item.prompt,
      meta: "Saved prompt",
    })),
    ...workflows.map((item) => ({
      id: item.key,
      tab: "workflows" as const,
      kicker: item.category || "Workflow",
      title: item.name,
      description: item.description || "Workspace workflow",
      meta: item.status || (item.custom ? "Private" : "Published"),
    })),
    ...playbooks.map((item) => ({
      id: item.key,
      tab: "playbooks" as const,
      kicker: item.practice_area || "Playbook",
      title: item.name,
      description: item.description || "Workspace playbook",
      meta: `${item.positions || item.topics?.length || 0} positions`,
    })),
  ];

  const renderScreen = () => {
    if (route.screen === "assistant") {
      const attachments = [
        ...assistantDocuments.map((document) => ({
          id: document.versionId,
          label: document.name,
          kind: "file" as const,
          onRemove: () => setAssistantDocuments((items) =>
            items.filter((item) => item.versionId !== document.versionId)
          ),
        })),
        ...sourceSelection.categoryIds.map((category) => ({
          id: `source:${category}`,
          label: category === "case" ? "US Case Law" : category === "web" ? "Web Search" : category === "regional" ? "Regional knowledge" : category === "edgar" ? "EDGAR" : playbooks.find((item) => item.key === sourceSelection.playbookId)?.name || "Playbook",
          kind: category === "playbooks" ? "playbook" as const : "jurisdiction" as const,
          onRemove: () => setSourceSelection((current) => ({
            ...current,
            categoryIds: current.categoryIds.filter((item) => item !== category),
          })),
        })),
      ];
      const repositoryRecords = pickerItems
        .filter((item) => item.tab === "repository" || item.source === "Request record")
        .slice(0, 30)
        .map((item) => ({
          id: item.versionId!,
          counterparty: item.name,
          status: item.source,
          meta: item.detail,
        }));
      return (
        <AssistantScreen
          value={assistantPrompt}
          onValueChange={setAssistantPrompt}
          busy={busy === "assistant"}
          attachments={attachments}
          projectName={project?.name}
          projectFileCount={attachments.length}
          onUploadFiles={uploadAssistantFiles}
          onAddFromProject={() => setPicker({ purpose: "assistant", tab: "projects" })}
          repositoryRecords={repositoryRecords}
          onAddRepositoryRecord={async (record) => {
            const target = await ensureProject();
            await documentIntelligenceApi.addVersion(target.id, record.id);
            await refreshProject(target.id);
            setAssistantDocuments((items) => [
              ...items.filter((item) => item.versionId !== record.id),
              { versionId: record.id, name: record.counterparty, source: record.status },
            ]);
            setNotice({ kind: "success", text: `${record.counterparty} added to the Assistant context.` });
          }}
          mentions={mentions}
          onMentionSelect={(mention) => setAssistantPrompt((value) => insertMention(value, mention.label))}
          onImprove={improvePrompt}
          prompts={prompts}
          onSavePrompt={savePrompt}
          onOpenPromptLibrary={() => navigateDocumentIntelligence({ screen: "sources" })}
          promptLibraryCount={prompts.length}
          onSubmit={(prompt) => submitAssistant(prompt)}
          sourceDialog={{
            playbooks: playbooks.map((item) => ({
              id: item.key,
              name: item.name,
              ruleCount: item.positions || item.topics?.length || 0,
              published: item.status !== "draft",
            })),
            availability: knowledgeAvailability,
            initialSelection: sourceSelection,
            onConfirm: setSourceSelection,
          }}
        />
      );
    }

    if (route.screen === "thread") {
      const messages = thread?.messages || [];
      const userMessage = [...messages].reverse().find((message) => message.role === "user");
      const prompt = userMessage?.content || thread?.title || "Document Intelligence request";
      const answer = [...messages].reverse().find((message) => message.role === "assistant");
      const isComplete = ["completed", "ready"].includes(answer?.status || "");
      // A thread parked in the retired awaiting-approval state counts as running: it is
      // being resumed, not waiting on the user.
      const isRunning = threadIsRunning;
      const followUpAttachments = assistantDocuments.map((document) => ({
        id: document.versionId,
        label: document.name,
        kind: "file" as const,
        onRemove: () => setAssistantDocuments((items) =>
          items.filter((item) => item.versionId !== document.versionId)
        ),
      }));
      const followUpRepositoryRecords = pickerItems
        .filter((item) => item.tab === "repository" || item.source === "Request record")
        .slice(0, 30)
        .map((item) => ({
          id: item.versionId!,
          counterparty: item.name,
          status: item.source,
          meta: item.detail,
        }));
      const citations: MappedThreadCitation[] = mapThreadCitations(answer);
      // Live narration while the run is in flight; for a thread whose run is over, the
      // same shape rebuilt from what the server stored. One input, so the screen cannot
      // hold two differing accounts of the same run.
      const hasLiveActivity = Boolean(activity.stages.length || activity.activity.length || activity.terminal);
      const activityState = hasLiveActivity
        ? activity
        : activityStateFromStoredProgress({
          steps: answer?.progress?.steps,
          events: answer?.progress?.events,
          status: answer?.status,
        });
      return (
        <ThreadScreen
          author={user?.name || "You"}
          time={thread?.created_at ? new Date(thread.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Now"}
          prompt={prompt}
          // Deliberately silent while the run is in flight. The activity panel is the ONE
          // place that says how the work is going; a second label sourced from the stored
          // message status disagrees with it for as long as a poll takes to land, and that
          // disagreement is the reported bug.
          statusLabel={answer?.status === "failed" ? "Needs attention" : isComplete ? "Completed" : ""}
          answerParagraphs={answer?.content && answer.status !== "running"
            ? answer.content.split(/\n{2,}/).filter(Boolean)
            : []}
          citations={citations}
          isRunning={isRunning}
          activity={activityState}
          activityConnection={hasLiveActivity || isRunning ? activityConnection : "closed"}
          showWorking={showWorking}
          onShowWorkingChange={changeShowWorking}
          onExit={() => navigateDocumentIntelligence({ screen: "assistant" })}
          onDelete={route.id ? async () => {
            await documentIntelligenceApi.deleteThread(route.id!);
            setThread(null);
            await refreshCatalog();
            navigateDocumentIntelligence({ screen: "assistant" });
          } : undefined}
          showNudge={false}
          followUpComposer={{
            busy: busy === "assistant" || isRunning,
            placeholder: "Ask a follow-up…",
            attachments: followUpAttachments,
            onUploadFiles: uploadAssistantFiles,
            onAddFromProject: () => setPicker({ purpose: "assistant", tab: "projects" }),
            projectName: project?.name,
            projectFileCount: followUpAttachments.length,
            repositoryRecords: followUpRepositoryRecords,
            onAddRepositoryRecord: async (record) => {
              const target = await ensureProject();
              await documentIntelligenceApi.addVersion(target.id, record.id);
              await refreshProject(target.id);
              setAssistantDocuments((items) => [
                ...items.filter((item) => item.versionId !== record.id),
                { versionId: record.id, name: record.counterparty, source: record.status },
              ]);
              setNotice({ kind: "success", text: `${record.counterparty} added to the thread context.` });
            },
            mentions,
            onMentionSelect: () => undefined,
            onImprove: improvePrompt,
            prompts,
            onSavePrompt: savePrompt,
            onOpenPromptLibrary: () => navigateDocumentIntelligence({ screen: "sources" }),
            promptLibraryCount: prompts.length,
            onSubmit: (value) => route.id ? submitAssistant(value, route.id) : undefined,
          }}
        />
      );
    }

    if (route.screen === "draft" && !route.id) {
      return (
        <BlankDraftScreen
          busy={busy === "upload" || busy === "draft-create"}
          activity={busy === "draft-create" ? draftActivity : null}
          selectedDraft={draftSource ? { name: draftSource.name, source: draftSource.source, previewText: draftPreview } : null}
          onImportDraft={importDraft}
          onChooseExistingDraft={() => setPicker({ purpose: "draft", tab: "repository" })}
          onExampleSelect={setDraftPrompt}
          composer={{
            value: draftPrompt,
            onValueChange: setDraftPrompt,
            busy: busy === "assistant" || busy === "draft-create",
            placeholder: "Describe what you want to draft or revise…",
            attachments: [
              ...(draftSource ? [{
                id: draftSource.versionId,
                label: draftSource.name,
                kind: "file" as const,
                onRemove: () => void removeProjectVersion(draftSource.versionId, draftSource.name),
              }] : []),
              ...draftContextSources.map((source) => ({
                id: source.versionId,
                label: source.name,
                kind: "file" as const,
                onRemove: () => void removeProjectVersion(source.versionId, source.name),
              })),
            ],
            onUploadFiles: uploadDraftFiles,
            onAddFromProject: () => setPicker({ purpose: "draft", tab: "projects" }),
            // WIRED BEFORE THE BUTTON WAS REMOVED, in that order and deliberately.
            // The "Choose from KontractAI" button on the draft screen was the ONLY
            // route to the repository from here — the Files menu offered it on the
            // assistant screen and not on this one. Deleting the button first would
            // have removed the capability while appearing to be a tidy-up.
            onAddRepositoryRecord: async (record: any) => {
              const target = await ensureProject();
              await documentIntelligenceApi.addVersion(target.id, record.id);
              await refreshProject(target.id);
              setDraftAttachments((current) => {
                if (current.some((item) => item.versionId === record.id)) return current;
                const merged: DraftSource[] = [...current, {
                  versionId: record.id,
                  name: record.counterparty,
                  source: record.status,
                  role: "unstated" as AttachmentRole,
                }];
                return merged.map((item, index) => (
                  index === 0 && item.role === "unstated" ? { ...item, role: "under_review" as AttachmentRole } : item
                ));
              });
              setNotice({ kind: "success", text: `${record.counterparty} attached to this draft.` });
            },
            // "The @ function should only reference the attached documents." — owner,
            // 2026-08-04. The shared `mentions` list also carries knowledge-source
            // categories and workflow templates. Those are meaningful on the assistant
            // screen and are noise here: on a draft, @ is how you say which of YOUR
            // documents is which, and offering anything else invites a mention that
            // binds no document. The assistant composer (above) keeps the full list.
            mentions: draftAttachments.map((item) => ({
              id: `file:${item.versionId}`,
              label: item.name,
              type: "File",
              detail: item.source,
            })),
            // An @-mention BINDS the document, it does not merely type its name. The
            // label goes into the prompt, and because `unaccountedFor` matches on that
            // label the attachment is thereby accounted for and will be sent. Before
            // this, picking from the @ menu inserted text and attached nothing — the
            // model never received the document the author had just named, and there
            // was no way to tell from the screen.
            onMentionSelect: (mention) => {
              setDraftPrompt((value) => insertMention(value, mention.label));
              if (mention.id?.startsWith("file:")) {
                const versionId = mention.id.slice(5);
                setDraftAttachments((current) => (
                  current.some((item) => item.versionId === versionId)
                    ? current
                    : [...current, { versionId, name: mention.label, source: "Mentioned in the instruction", role: "reference" as AttachmentRole }]
                ));
              }
            },
            onImprove: improvePrompt,
            prompts,
            onSavePrompt: savePrompt,
            onOpenPromptLibrary: () => navigateDocumentIntelligence({ screen: "sources" }),
            promptLibraryCount: prompts.length,
            onSubmit: submitDraft,
          }}
        />
      );
    }

    if (route.screen === "draft") {
      const changes: DraftChange[] = (redline?.changes || redline?.operations || []).map((item: any, index: number): DraftChange => ({
        id: String(item.id || `change-${index + 1}`),
        introducedInVersion: 2,
        position: item.risk || item.risk_level || "Review",
        clause: item.category || item.type || `Change ${index + 1}`,
        rationale: item.justification || item.rationale || item.summary || "Proposed by the redline review.",
        // The rebuilt engine grounds every operation in the exact source text it
        // matched (expected_text) plus the playbook clause it applied.
        grounding: item.citation?.quote || item.citation?.snippet
          || [item.playbook_citation, item.expected_text].filter(Boolean).join(" — ")
          || "Grounded in the selected source document.",
        // v1 records `status`, the rebuilt engine records `decision`.
        // `modified` IS A DECIDED STATE, and reading it as open refuses an
        // export the server would have produced.
        //
        // reviewService.js counts pending as `decision === 'pending'` and puts
        // `modified` in the clean-copy set alongside `accepted` — a reviewer who
        // edited the proposed wording has decided, they just decided something
        // other than the model's text. Mapping it to "open" made the screen
        // refuse for a change the server considers settled: the mirror image of
        // the defect in #468, the dialog refusing what the server allows.
        //
        // It renders as accepted because that is what it is on the way out: the
        // reviewer's own wording goes into the document.
        // ALL FIVE SERVER DECISIONS ARE HANDLED, AND ONLY `pending` IS OPEN.
        //
        // `redlineV2/schemas.js` DECISIONS is
        //   pending | accepted | rejected | modified | manual_required
        // and `reviewService.js` treats exactly `pending` as undecided while
        // applying exactly `accepted` and `modified`. So every other value is
        // settled and will not appear in the document.
        //
        // `manual_required` was the one this screen got wrong, found by an
        // independent verifier: it fell through to "open", which held the export
        // gate shut on a change the server neither waits for nor applies — and
        // no amount of clicking could clear it, because the screen never sends
        // that value. Same defect class as #469, still live after it.
        //
        // It is mapped to "rejected" because that is the CONSEQUENCE they share:
        // settled, and not in the output. The label is imprecise — the reviewer
        // did not reject it, the engine declined to edit that paragraph
        // automatically — and this screen has only three statuses. A fourth,
        // "needs doing by hand", is a change to what the product shows the user
        // and is the owner's to make; it is not smuggled in here.
        status: ["accepted", "modified"].includes(item.status || item.decision) ? "accepted"
          : (item.status || item.decision) === "pending" || !(item.status || item.decision) ? "open"
            : "rejected",
        // A comment is decidable like any operation but must read as what it
        // is: an annotation. The card labels it and the document body never
        // renders markup for it (BCG Finding 1).
        isComment: item.action === "comment_range",
      }));
      const operations = redline?.changes || redline?.operations || [];
      // ONLY OPERATIONS THE EXPORT WOULD PUT IN THE DOCUMENT drive markup
      // (owner-reported BCG regression, Finding 1). The server computes
      // `applies_to_tracked_text` from the SAME rule the export uses
      // (applySets.js); this screen renders that answer instead of its own.
      // The concrete failure this closes: a comment_range operation spanning
      // the party preamble rendered here as a full strikethrough — the owner
      // saw the parties "deleted" while the exported file, correctly, carried
      // them intact with a comment. A comment annotates; it never strikes.
      // Refused operations never reach `operations` at all (they are in
      // dropped_changes, rendered separately and never as markup); v1 payloads
      // carry no flag and no comment_range action, so they pass unchanged.
      const marksTrackedText = (item: any) => item.action !== "comment_range" && item.applies_to_tracked_text !== false;
      const firstChangeByAnchor = new Map<string, any>();
      operations.filter(marksTrackedText).forEach((item: any) => {
        if (item.anchor_id && !firstChangeByAnchor.has(item.anchor_id)) firstChangeByAnchor.set(item.anchor_id, item);
      });
      const previewParagraphs = Array.isArray(redline?.source_preview) ? redline.source_preview : [];
      const clauses: DraftClause[] = previewParagraphs.length
        ? previewParagraphs.map((paragraph: any, index: number): DraftClause => {
          const item = firstChangeByAnchor.get(paragraph.anchor_id);
          const text = String(paragraph.text || "");
          // v1 emits `start`/`end`; the rebuilt engine emits `start_offset`/
          // `end_offset`. Reading only the v1 names collapsed every rebuilt-engine
          // edit to offset 0, which renders the whole paragraph as deleted.
          const rawStart = item ? (item.start ?? item.start_offset) : undefined;
          const rawEnd = item ? (item.end ?? item.end_offset) : undefined;
          const start = item ? Math.max(0, Number(rawStart) || 0) : 0;
          const end = item ? Math.max(start, Number(rawEnd) || start) : 0;
          return {
            id: String(paragraph.anchor_id || `paragraph-${index + 1}`),
            // THE DOCUMENT'S OWN CLAUSE LABEL, OR NOTHING AT ALL.
            //
            // This was `String(index + 1)` — the position of the row in an array,
            // rendered as though it were the contract's numbering. A oneNDA whose
            // clauses are (a), (i), (ii) came back as 1, 2, 3, and the sub-clause
            // lettering vanished entirely. That is not a missing feature, it is a
            // confidently wrong statement about a customer's agreement, and it is
            // worse than showing nothing.
            //
            // The parser resolves these correctly from w:numPr and numbering.xml;
            // the value was being computed and then discarded before it reached here.
            // A paragraph with no number now renders with NO number.
            number: paragraph.number_text ? String(paragraph.number_text) : undefined,
            // `paragraph.part` is the OOXML ZIP entry name — "word/document.xml" —
            // which is what appeared as a section heading on the owner's screen. The
            // old guard excluded 'Document', the fallback value that never occurs,
            // and passed the real one that always does: it filtered the impossible
            // case and missed the certain one. No internal part name is displayed.
            heading: undefined,
            before: item ? text.slice(0, start) : "",
            original: item ? text.slice(start, end) : text,
            replacement: item ? String(item.proposed_text || item.replacement_text || "") : text,
            after: item ? text.slice(end) : "",
            changeId: item ? String(item.id) : undefined,
          };
        })
        : operations.map((item: any, index: number): DraftClause => ({
          id: String(item.id || `clause-${index + 1}`),
          // Same rule on this branch: no document numbering is available here at all,
          // so none is shown. `heading` is a real category from the change itself,
          // not an internal file name, so it stays.
          number: undefined,
          heading: item.category || item.type || "Proposed revision",
          before: "",
          original: item.original_text || item.summary || "Original wording",
          replacement: item.proposed_text || item.summary || "Proposed wording",
          after: "",
          changeId: String(item.id || `change-${index + 1}`),
        }));
      const reviewId = redline?.review?.id || route.id || "";
      const filename = redline?.output?.filename || redline?.artifact?.filename || "document-intelligence-redline.docx";
      return (
        <LoadedDraftScreen
          // THE HEADING IS THE DOCUMENT, NOT THE INSTRUCTION.
          //
          // This was the raw instruction text, so the owner's own prompt — "Revise
          // @oneNDA_v2.1.docx.to align with @CDA Playbook.docx. Reflects all
          // revisions in redline format." — became the &lt;h1&gt; of his contract review,
          // @-mentions, run-on typo and all. A review of a document should be headed
          // by the document.
          //
          // redline.source.filename was already reaching the frontend and unused.
          title={redline?.source?.filename || redline?.review?.source_filename || "Document review"}
          // The number of documents the review actually had in front of it, so the
          // header can stop asserting a playbook was involved when none was.
          referenceCount={Array.isArray(redline?.review?.precedent_version_ids)
            ? redline.review.precedent_version_ids.length
            : (redline?.review?.precedents?.length || 0)}
          filename={filename}
          documentMeta={`${changes.length} proposed change${changes.length === 1 ? "" : "s"}`}
          versions={[
            { id: "original", number: 1, summary: "Original document", byline: "Source version" },
            { id: "working", number: 2, summary: "Document Intelligence review", byline: user?.name || "Workspace" },
          ]}
          workingVersion={2}
          clauses={clauses}
          changes={changes}
          playbookName="Review instructions"
          playbookMet={changes.filter((item) => item.status !== "open").length}
          playbookTotal={changes.length}
          // A MODULE CONSTANT, NOT AN INLINE LITERAL. The literal was a new
          // array on every render of this screen, and the export dialog had it
          // in an effect's dependency list — so every render reset the dialog
          // while the user was standing in it. The dialog no longer depends on
          // this identity (see ExportWordDialog), and it should not have to:
          // a value that never changes should not look like it changes.
          exportFormats={EXPORT_FORMATS}
          verifyGate={!changes.some((item) => item.status === "open")}
          conversation={redline?.conversation || []}
          reviseComposer={{
            busy: busy === "draft-revise",
            placeholder: "Ask about this draft, or describe another revision…",
            onImprove: improvePrompt,
            prompts,
            onSavePrompt: savePrompt,
            onOpenPromptLibrary: () => navigateDocumentIntelligence({ screen: "sources" }),
            promptLibraryCount: prompts.length,
          }}
          onRevise={async (prompt, mode) => {
            setBusy("draft-revise");
            try {
              if (mode === "answer") {
                setRedline(await documentIntelligenceApi.askRedline(reviewId, prompt, redline?.review?.revision));
              } else {
                const result = await documentIntelligenceApi.createRedline({
                  workspace_id: redline?.review?.workspace_id,
                  version_id: redline?.review?.version_id,
                  instructions: prompt,
                });
                const nextId = result?.review?.id;
                if (!nextId) throw new Error("The revised draft did not return an identifier.");
                await refreshCatalog();
                navigateDocumentIntelligence({ screen: "draft", id: nextId });
              }
            } catch (reason) {
              setNotice({ kind: "error", text: errorMessage(reason, "The draft follow-up could not be completed.") });
            } finally {
              setBusy("");
            }
          }}
          onDecideAll={async (decision) => {
            setBusy("draft-decide-all");
            try {
              if (redline?.engine === "v2") {
                await documentIntelligenceApi.redlineV2DecideAll(reviewId, decision);
                setRedline({ ...(await documentIntelligenceApi.redlineV2Review(reviewId)), engine: "v2" });
                return;
              }
              await documentIntelligenceApi.decideAllRedlineChanges(reviewId, decision, redline?.review?.revision);
              setRedline(await documentIntelligenceApi.redline(reviewId));
            } catch (reason) {
              setNotice({ kind: "error", text: errorMessage(reason, "The changes could not be decided.") });
              throw reason;
            } finally {
              setBusy("");
            }
          }}
          onChangeDecision={async (change, decision) => {
            setBusy(`draft-${decision}`);
            try {
              if (redline?.engine === "v2") {
                await documentIntelligenceApi.redlineV2Decide(reviewId, change.id, decision === "accept" ? "accepted" : "rejected");
                setRedline({ ...(await documentIntelligenceApi.redlineV2Review(reviewId)), engine: "v2" });
                return;
              }
              await documentIntelligenceApi.decideRedlineChange(
                reviewId,
                change.id,
                decision,
                redline?.review?.revision
              );
              setRedline(await documentIntelligenceApi.redline(reviewId));
            } catch (reason) {
              // A refused decision must say so, not vanish. Rethrown so the
              // accept-all loop stops at the change that refused.
              setNotice({ kind: "error", text: errorMessage(reason, "The decision could not be saved.") });
              throw reason;
            } finally {
              setBusy("");
            }
          }}
          hasArtifacts={(redline?.artifacts || []).length > 0}
          // WHICH ENGINE IS BEHIND THIS DIALOG DECIDES WHAT IT MAY OFFER.
          //
          // The rebuilt engine lets a tracked export carry undecided changes as
          // proposals. The original engine refuses generation outright while any
          // change is undecided, in every mode — and this screen falls back to it
          // (see the `.catch()` on the redlineV2 load above) whenever the rebuilt
          // engine is unavailable, which includes every estate where
          // REDLINE_V2_ENABLED is off. Flags land off, so that is the default.
          //
          // Offering the tracked export there would enable a button, state in
          // writing that undecided changes travel as proposals, and then 409.
          // Anything that is not positively the rebuilt engine is treated as the
          // stricter one.
          trackedNeedsDecisions={redline?.engine !== "v2"}
          droppedChanges={redline?.dropped_changes || []}
          memo={redline?.general_review || null}
          onDownload={() => downloadUrl(
            redline?.engine === "v2" && (redline?.artifacts || [])[0]
              ? `/api/redline-v2/${encodeURIComponent(reviewId)}/artifacts/${encodeURIComponent(redline.artifacts[0].id)}/download`
              : `/api/docint/redlines/${encodeURIComponent(reviewId)}/download`,
            (redline?.engine === "v2" && (redline?.artifacts || [])[0]?.filename) || filename)
            // Silent unhandled rejection was "the Download button does not
            // work". Every failure now lands in the notice bar, in the
            // server's own words.
            .catch((reason) => setNotice({ kind: "error", text: errorMessage(reason, "The download failed.") }))}
          onExport={async (request) => {
            if (redline?.engine === "v2") {
              // The rebuilt engine's modes: tracked markup for the counterparty,
              // or the accepted text with the decisions applied and no markup.
              // INTERNAL, not external. "Apply my decisions and export clean"
              // is the author's own copy. `clean_external` is a different thing
              // — a release CANDIDATE for the counterparty, which is created
              // `generated_unreleased` and deliberately cannot be downloaded
              // until it has been through release and attestation. Asking for
              // it here produced exactly that refusal, surfacing as "the
              // download could not be prepared". Sending an external copy out
              // is a separate, deliberate act, and it keeps its own gate.
              const mode = request.changes === "clean" ? "clean_internal" : "tracked_internal";
              await documentIntelligenceApi.redlineV2Generate(reviewId, mode);
              const refreshedV2 = { ...(await documentIntelligenceApi.redlineV2Review(reviewId)), engine: "v2" };
              setRedline(refreshedV2);
              // The artifact for the mode just asked for — not simply the newest.
              // A review can hold several, and handing back whichever happens to
              // sort first would give the reviewer a document they did not ask
              // for, with no way to tell.
              const artifactV2 = (refreshedV2?.artifacts || []).find((entry: any) => entry.mode === mode)
                || (refreshedV2?.artifacts || [])[0];
              if (!artifactV2) throw new Error("The Word document was generated but could not be located for download.");
              await downloadUrl(
                `/api/redline-v2/${encodeURIComponent(reviewId)}/artifacts/${encodeURIComponent(artifactV2.id)}/download`,
                artifactV2.filename || filename
              );
              return { filename: artifactV2.filename || filename };
            }
            // The dialog's choice maps to a REAL artifact: generation produces
            // both the tracked redline and the accepted-text (clean) document,
            // and the choice picks which one lands in the user's downloads.
            // The choice used to be ignored entirely ("none of the options
            // work"). Errors propagate — the dialog renders them.
            await documentIntelligenceApi.generateRedline(reviewId, redline?.review?.revision);
            const refreshed = await documentIntelligenceApi.redline(reviewId);
            setRedline(refreshed);
            const wantedLabel = request.changes === "clean" ? "accepted-text" : "tracked";
            const artifact = (refreshed?.artifacts || []).find((entry: any) => entry.label === wantedLabel)
              || (refreshed?.artifacts || [])[0];
            if (!artifact) throw new Error("The Word document was generated but could not be located for download.");
            await downloadUrl(
              `/api/docint/redlines/${encodeURIComponent(reviewId)}/artifacts/${encodeURIComponent(artifact.id)}/download`,
              artifact.filename || filename
            );
            return { filename: artifact.filename || filename };
          }}
        />
      );
    }

    if (route.screen === "table" && !route.id) {
      return (
        <ReviewTableSetup
          documents={reviewDocuments}
          settings={defaultTableSettings}
          busy={busy === "table-create" || busy === "upload"}
          onFilesSelected={(files) => uploadToProject(files)}
          onRequestSource={(source) => setPicker({
            purpose: "review",
            tab: source === "project" ? "projects" : "repository",
          })}
          onCancel={() => navigateDocumentIntelligence({ screen: "assistant" })}
          onCreateTable={async (input) => {
            setBusy("table-create");
            try {
              const target = await ensureProject();
              const review = await documentIntelligenceApi.createTable(target.id, {
                name: input.name || "Untitled review",
                document_version_ids: input.documentIds,
                columns: [{ key: "summary", label: "Summary", prompt: "Summarise the key provision and cite the source.", format: "summary" }],
                table_settings: tableSettingsToApi(input.settings),
              });
              setTable({ ...review, execution: null });
              setReviewDocuments([]);
              await refreshCatalog();
              navigateDocumentIntelligence({ screen: "table", id: review.id });
            } catch (reason) {
              setNotice({ kind: "error", text: errorMessage(reason, "The review table could not be created.") });
            } finally {
              setBusy("");
            }
          }}
        />
      );
    }

    if (route.screen === "table") {
      const columns = (table?.columns || []).map(mapReviewColumn);
      const execution = table?.execution;
      const mappedRows = mapTableRows(table, project?.versions, execution?.status);
      return (
        <ReviewTableWorkspace
          name={table?.name || "Review table"}
          documents={mappedRows.map((row) => row.document)}
          columns={columns}
          rows={mappedRows}
          settings={tableSettingsFromApi(table?.table_settings)}
          runState={{
            status: ["pending", "running"].includes(execution?.status) ? "running" : execution?.status === "failed" ? "failed" : ["completed", "succeeded"].includes(execution?.status) ? "ready" : "idle",
            progress: execution?.progressSummary?.percent_complete,
            label: execution?.status,
          }}
          onSuggestColumns={async (prompt) => (await documentIntelligenceApi.suggestColumns(prompt)).map(mapReviewColumn)}
          onRequestAddFile={(source) => setPicker({
            purpose: "review",
            tab: source === "project" ? "projects" : source === "repository" ? "repository" : "recent",
          })}
          onAddColumns={async (next) => {
            const review = await documentIntelligenceApi.updateTable(table.id, table.revision, {
              columns: [...columns, ...next].map((column) => ({
                key: column.id,
                label: column.title,
                prompt: column.query,
                format: column.type.toLowerCase().replace(/\W+/g, "_"),
              })),
            });
            setTable(review);
          }}
          onUpdateSettings={async (settings) => setTable(await documentIntelligenceApi.updateTable(table.id, table.revision, { table_settings: tableSettingsToApi(settings) }))}
          onUpdateColumns={async (next) => setTable(await documentIntelligenceApi.updateTable(table.id, table.revision, {
            columns: next.map((column) => ({ key: column.id, label: column.title, prompt: column.query, format: "summary" })),
          }))}
          onRun={async () => {
            try {
              const started = await documentIntelligenceApi.startTableExecution(table.id);
              setTable((current: any) => ({ ...current, execution: started }));
              await refreshCatalog();
            } catch (reason) {
              setNotice({ kind: "error", text: errorMessage(reason, "The review table could not be run.") });
            }
          }}
          onStop={execution ? async () => {
            const stopped = await documentIntelligenceApi.stopTableExecution(table.id, execution.revision);
            setTable({ ...(await documentIntelligenceApi.table(table.id)), execution: stopped });
          } : undefined}
          onExport={() => downloadUrl(`/api/docint/tabular-reviews/${encodeURIComponent(table.id)}/export.xlsx`, `${table.name || "review"}.xlsx`)}
          onAskDI={(prompt) => submitAssistant(prompt)}
        />
      );
    }

    if (route.screen === "sources") {
      return <SourcesLibrary items={sourceItems} onOpenItem={(item) => {
        if (item.tab === "prompts") {
          const prompt = prompts.find((candidate) => candidate.id === item.id);
          if (prompt) setAssistantPrompt(prompt.prompt);
          navigateDocumentIntelligence({ screen: "assistant" });
        } else if (item.tab === "playbooks") {
          navigateDocumentIntelligence({ screen: "playbook", id: item.id });
        } else {
          navigateDocumentIntelligence({ screen: "workflow-builder", id: item.id });
        }
      }} />;
    }

    if (route.screen === "playbooks") {
      return (
        <PlaybooksList
          playbooks={playbookRows}
          currentUser={user?.name || undefined}
          onCreate={() => navigateDocumentIntelligence({ screen: "playbook-create" })}
          onSelectPlaybook={(item) => navigateDocumentIntelligence({ screen: "playbook", id: item.id })}
        />
      );
    }

    if (route.screen === "playbook-create") {
      return (
        <PlaybookCreationFlow
          authorName={user?.name || "You"}
          busy={busy === "upload"}
          onCancel={() => navigateDocumentIntelligence({ screen: "playbooks" })}
          onUploadFiles={(_questionIndex, files) => uploadToProject(files)}
          onBuildPlaybook={(input) => {
            const positions = input.answers.filter((answer) => !answer.skipped).map((answer, index) => ({
              id: `draft-position-${index + 1}`,
              number: String(index + 1).padStart(2, "0"),
              name: answer.text || `Position derived from question ${answer.questionIndex + 1}`,
              standard: "Review and confirm the preferred position.",
              fallback: "Review and confirm an acceptable fallback.",
              escalation: "Escalate material departures to the matter lead.",
              origin: input.materials.length ? "Uploaded materials" : "Creation workflow",
            }));
            setPlaybookPositions(positions);
            navigateDocumentIntelligence({ screen: "playbook", id: "new" });
          }}
        />
      );
    }

    if (route.screen === "playbook") {
      const selected = playbooks.find((item) => item.key === route.id);
      const positions = selected?.topics?.map((topic, index) => ({
        id: topic.key,
        number: String(index + 1).padStart(2, "0"),
        name: topic.label,
        standard: topic.standard || "",
        fallback: topic.fallback || "",
        escalation: topic.escalation || "",
        origin: selected.name,
      })) || playbookPositions;
      return (
        <PlaybookPositionsEditor
          title={selected?.name || "New playbook — draft"}
          mode={selected ? "existing" : "workflow"}
          positions={positions}
          onBack={() => navigateDocumentIntelligence({ screen: "playbooks" })}
          onUpsertPosition={(next) => setPlaybookPositions((items) => [next, ...items.filter((item) => item.id !== next.id)])}
        />
      );
    }

    if (route.screen === "workflows") {
      return <WorkflowsScreen workflows={workflows} busy={busy === "workflow"} onCreate={() => navigateDocumentIntelligence({ screen: "workflow-builder" })} onOpen={(item) => navigateDocumentIntelligence({ screen: "workflow-builder", id: item.key })} onRun={(item) => navigateDocumentIntelligence({ screen: "workflow-run", id: item.key })} />;
    }

    if (route.screen === "workflow-builder") {
      const selected = workflows.find((item) => item.key === route.id);
      const persistWorkflow = async (input: WorkflowBuilderSaveInput) => {
        const body = {
          title: input.name,
          description: input.answers.filter(Boolean).join(" "),
          type: input.answers[2]?.toLowerCase().includes("table") ? "tabular" : "assistant",
          visibility: "personal",
          instructions_markdown: input.blocks.map((block) => `## ${block.name}\n${block.prompt || block.output}`).join("\n\n"),
          columns: input.answers[2]?.toLowerCase().includes("table")
            ? input.blocks
              .filter((block) => block.kind === "AI action" || block.kind === "Response")
              .map((block) => ({
                key: block.id,
                label: block.name,
                prompt: block.prompt || `Produce ${block.name}.`,
                format: block.kind === "Response" ? "bullets" : "free_text",
                required: block.kind === "Response",
              }))
            : undefined,
        };
        const workflowId = selected?.custom
          ? selected.id || selected.key.replace(/^custom:/, "")
          : null;
        const result = await apiCallEx(
          workflowId
            ? `/docint/workflows/custom/${encodeURIComponent(workflowId)}`
            : "/docint/workflows/custom",
          workflowId
            ? {
              method: "PATCH",
              body: JSON.stringify({ ...body, expected_revision: selected?.revision }),
            }
            : { method: "POST", body: JSON.stringify(body) },
          "return"
        );
        if (!result.ok) throw new Error(result.data?.error || "The workflow could not be saved.");
        return result.data?.workflow;
      };
      const saveWorkflow = async (input: WorkflowBuilderSaveInput) => {
        setBusy("workflow");
        try {
          const saved = await persistWorkflow(input);
          await refreshCatalog();
          setNotice({ kind: "success", text: "Workflow saved as a private draft." });
          if (!selected?.custom && saved?.id) {
            navigateDocumentIntelligence({ screen: "workflow-builder", id: `custom:${saved.id}` });
          }
        } catch (reason) {
          setNotice({ kind: "error", text: errorMessage(reason, "The workflow could not be saved.") });
        } finally {
          setBusy("");
        }
      };
      const testWorkflow = async (input: WorkflowBuilderSaveInput) => {
        setBusy("workflow");
        try {
          const saved = await persistWorkflow(input);
          await refreshCatalog();
          const key = saved?.id ? `custom:${saved.id}` : selected?.key;
          if (!key) throw new Error("Save the workflow before testing it.");
          navigateDocumentIntelligence({ screen: "workflow-run", id: key });
        } catch (reason) {
          setNotice({ kind: "error", text: errorMessage(reason, "The workflow could not be prepared for testing.") });
        } finally {
          setBusy("");
        }
      };
      const submitWorkflow = async (input: WorkflowBuilderSaveInput) => {
        setBusy("workflow");
        try {
          const saved = await persistWorkflow(input);
          const workflowId = saved?.id || selected?.id || selected?.key.replace(/^custom:/, "");
          if (!workflowId) throw new Error("Save the workflow before submitting it.");
          const result = await apiCallEx(`/docint/workflows/custom/${encodeURIComponent(workflowId)}/submit`, {
            method: "POST",
            body: JSON.stringify({ attribution: { mode: "anonymous" } }),
          }, "return");
          if (!result.ok) throw new Error(result.data?.error || "The workflow could not be submitted.");
          await refreshCatalog();
          setNotice({ kind: "success", text: "Workflow submitted for workspace approval." });
        } catch (reason) {
          setNotice({ kind: "error", text: errorMessage(reason, "The workflow could not be submitted.") });
        } finally {
          setBusy("");
        }
      };
      return (
        <WorkflowBuilderScreen
          initialWorkflow={selected}
          busy={busy === "workflow"}
          onBack={() => navigateDocumentIntelligence({ screen: "workflows" })}
          onSave={saveWorkflow}
          onTest={testWorkflow}
          onPublish={submitWorkflow}
        />
      );
    }

    if (route.screen === "workflow-run") {
      const selected = workflows.find((item) => item.key === route.id);
      return (
        <WorkflowRunScreen
          workflowName={selected?.name || "Workflow"}
          playbookOptions={playbooks.map((item) => ({ id: item.key, name: item.name }))}
          onBack={() => navigateDocumentIntelligence({ screen: "workflows" })}
          onSubmit={(input: WorkflowRunSubmitInput) => submitWorkflowRun({
            workflowKey: route.id || "",
            uploadFiles: uploadToProject,
            ensureProject,
            refreshProject,
            post: (path, body) => apiCallEx(path, { method: "POST", body: JSON.stringify(body) }, "return"),
          }, input)}
        />
      );
    }

    if (route.screen === "projects") {
      return <ProjectsScreen projects={projects} busy={busy === "project-delete"} onNew={() => navigateDocumentIntelligence({ screen: "project-new" })} onOpen={(item) => navigateDocumentIntelligence({ screen: "project", id: item.id })} onDelete={setDeleteTarget} />;
    }

    if (route.screen === "project-new") {
      return (
        <ProjectNewScreen
          sources={sourceModes}
          busy={busy === "project-create"}
          onBack={() => navigateDocumentIntelligence({ screen: "projects" })}
          onCreate={async (input) => {
            setBusy("project-create");
            try {
              const created = await documentIntelligenceApi.createProject({ name: input.name, description: input.description, client_id: input.clientId });
              if (input.files.length) await documentIntelligenceApi.uploadFiles(created.id, input.files.slice(0, 50));
              await refreshCatalog();
              navigateDocumentIntelligence({ screen: "project", id: created.id });
            } catch (reason) {
              setNotice({ kind: "error", text: errorMessage(reason, "The project could not be created.") });
            } finally {
              setBusy("");
            }
          }}
        />
      );
    }

    if (route.screen === "project" && project) {
      return (
        <ProjectDetailScreen
          project={project}
          threads={threads}
          busy={busy === "project" || busy === "upload"}
          onBack={() => navigateDocumentIntelligence({ screen: "projects" })}
          onAddFiles={uploadToProject}
          onNewConversation={() => navigateDocumentIntelligence({ screen: "assistant", workspaceId: project.id })}
          onNewTable={() => {
            setReviewDocuments((project.versions || []).map((version) => ({
              id: version.version_id,
              name: version.filename || "Project document",
              origin: "project",
            })));
            navigateDocumentIntelligence({ screen: "table", workspaceId: project.id });
          }}
          onNewDraft={() => navigateDocumentIntelligence({ screen: "draft", workspaceId: project.id })}
          onOpenThread={(item) => navigateDocumentIntelligence({ screen: "thread", id: item.id })}
          onAsk={(question) => documentIntelligenceApi.askProject(project.id, {
            question,
            document_version_ids: (project.versions || []).map((version) => version.version_id),
            source_modes: ["selected_documents"],
          })}
        />
      );
    }

    if (route.screen === "history") {
      return <HistoryScreen items={history} onOpen={(item) => {
        if (item.type === "thread") navigateDocumentIntelligence({ screen: "thread", id: item.id });
        else if (item.type === "review_table") navigateDocumentIntelligence({ screen: "table", id: item.id });
        else if (item.type === "redline") navigateDocumentIntelligence({ screen: "draft", id: item.id });
        else if (item.type === "workflow") navigateDocumentIntelligence({ screen: "workflow-run", id: item.id });
      }} />;
    }

    if (route.screen === "settings") {
      return <SettingsScreen sources={sourceModes} />;
    }

    return <div className="di-redesign-loading">Loading Document Intelligence…</div>;
  };

  return (
    <div className="di-redesign">
      {notice ? (
        <div className={`di-redesign-notice di-redesign-notice--${notice.kind}`} role={notice.kind === "error" ? "alert" : "status"}>
          <span>{notice.text}</span>
          <button type="button" aria-label="Dismiss message" onClick={() => setNotice(null)}>×</button>
        </div>
      ) : null}
      {renderScreen()}
      {picker ? (
        <div className="di-redesign-picker" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setPicker(null)}>
          <div role="dialog" aria-modal="true" aria-label="Choose documents from KontractAI">
            <DocumentIntelligenceSourcePicker
              key={`${picker.purpose}:${picker.tab}`}
              items={pickerItems}
              availability={pickerAvailability}
              initialTab={picker.tab}
              visibleTabs={["recent", "library", "projects", "repository"]}
              busy={busy === "source-picker" || busy === "upload"}
              title={picker.purpose === "draft"
                ? "Choose a draft from the Repository or a request record"
                : picker.purpose === "review"
                  ? "Add documents to this review table"
                  : "Add documents to Assistant"}
              confirmLabel={picker.purpose === "draft" ? "Use this draft" : "Add selected"}
              onUploadFiles={picker.purpose === "draft" ? async (files) => {
                await uploadDraftFiles(files);
                setPicker(null);
              } : picker.purpose === "review" && route.screen === "table" && route.id ? async (files) => {
                await uploadReviewFiles(files);
                setPicker(null);
              } : uploadToProject}
              onUploadFolder={picker.purpose === "draft" ? async (files) => {
                await uploadDraftFiles(files);
                setPicker(null);
              } : picker.purpose === "review" && route.screen === "table" && route.id ? async (files) => {
                await uploadReviewFiles(files);
                setPicker(null);
              } : uploadToProject}
              onCancel={() => setPicker(null)}
              onConfirm={usePickedDocuments}
            />
          </div>
        </div>
      ) : null}
      {deleteTarget ? (
        <div className="di-redesign-dialog" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setDeleteTarget(null)}>
          <section role="dialog" aria-modal="true" aria-labelledby="di-delete-title">
            <span className="di-redesign-kicker">Permanent action</span>
            <h2 id="di-delete-title">Delete {deleteTarget.name}?</h2>
            <p>This removes the project workspace and its Document Intelligence activity. Canonical Repository documents are not deleted.</p>
            <div>
              <button type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" className="is-danger" disabled={busy === "project-delete"} onClick={() => {
                void (async () => {
                  setBusy("project-delete");
                  try {
                    await documentIntelligenceApi.deleteProject(deleteTarget.id);
                    setProjects((items) => items.filter((item) => item.id !== deleteTarget.id));
                    setDeleteTarget(null);
                    setNotice({ kind: "success", text: "Project deleted." });
                  } catch (reason) {
                    setNotice({ kind: "error", text: errorMessage(reason, "The project could not be deleted.") });
                  } finally {
                    setBusy("");
                  }
                })();
              }}>Delete project</button>
            </div>
          </section>
        </div>
      ) : null}
      {ruleSetGate ? (
        <div className="di-redesign-dialog" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="di-ruleset-title" style={{ maxWidth: 760 }}>
            <span className="di-redesign-kicker">Approval needed before the review can run</span>
            <h2 id="di-ruleset-title">Approve how {ruleSetGate.playbookName} will be applied</h2>
            {!ruleSetGate.ruleSet ? (
              <>
                <p>
                  Reviews run only against an approved reading of the playbook — never against one
                  produced on the fly. No approved reading exists for this playbook yet. Compile it
                  first: you will see every rule, its weight, and its fallback positions in plain
                  English before anything is approved.
                </p>
                <div>
                  <button type="button" onClick={() => setRuleSetGate(null)}>Not now</button>
                  <button type="button" disabled={busy === "rule-set-compile"} onClick={() => void compileGateRuleSet()}>
                    {busy === "rule-set-compile" ? "Reading the playbook…" : "Read the playbook's rules"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  This is the reading the review will enforce — {ruleSetGate.ruleSet.rule_count ?? (ruleSetGate.ruleSet.rules_json?.rules || []).length} rules
                  compiled from {ruleSetGate.playbookName}. Approving freezes it: every review of this
                  playbook applies exactly these rules until a new version is approved.
                </p>
                {ruleSetGate.diffAgainst && ruleSetGate.diff ? (
                  <p>
                    Compared with the previously approved version{ruleSetGate.diff.identical
                      ? ", the rules are identical."
                      : `: ${ruleSetGate.diff.added?.length || 0} added, ${ruleSetGate.diff.removed?.length || 0} removed, ${ruleSetGate.diff.changed?.length || 0} changed.`}
                  </p>
                ) : (
                  <p>This is the first reading of this playbook — there is no earlier approved version to compare against.</p>
                )}
                <pre style={{ maxHeight: "45vh", overflow: "auto", whiteSpace: "pre-wrap", textAlign: "left", fontSize: "0.85em", border: "1px solid var(--di-line)", padding: "0.75rem" }}>
                  {ruleSetGate.rendering}
                </pre>
                <textarea
                  rows={2}
                  placeholder="Approval note (optional) — recorded with the approval"
                  value={ruleSetGate.note}
                  onChange={(event) => { const value = event.target.value; setRuleSetGate((gate) => gate ? { ...gate, note: value } : gate); }}
                  style={{ width: "100%" }}
                />
                <div>
                  <button type="button" onClick={() => setRuleSetGate(null)}>Not now</button>
                  <button type="button" disabled={busy === "rule-set-approve"} onClick={() => void approveGateRuleSet()}>
                    {busy === "rule-set-approve" ? "Approving…" : "Approve and run the review"}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default DocumentIntelligenceRedesign;
